import ko from 'knockout';
import $ from 'jquery';
import _ from 'lodash';
import DataProvider from './data-framework/data-provider';
import DataRepository from './data-framework/data-repository';
import SystemTypes from './helpers/system-types';

var REALTIME_SYSTEM_TYPES = SystemTypes.getRealtimeSystemTypesString();

var editColumnsLink = "<span>Edit</span>";

/**
 * Panel Template
 *
 * {
 *    panelType:            Type of panel to show
 *    widgetType:           Type of widget
 *    title:                Panel Title
 *    width:                Panel Width
 *    height:               Panel Height
 *    header:               (optional) Panel Header
 *    footer:               (optional) Panel Footer
 *    image:                Image shown on Panel Selection modal
 *    description:          Description shown on Panel Selection modal
 *    maxCount:             Maximum number of panels that can be added
 *    panelSettings:        (optionsl) Panel configuration settings (non-user defined)
 *    optionsComp:          (optional) Options component
 *    defaultOptions:       (optional) Default panel options
 *    dataSources:          (optional) Data sources that populate the panel
 *    dataFormatter:        (optional) Data formatters
 *    fixedPanel:           (optional) Indicates if panel can be moved
 *    optionsLink:          (optional) Edit options link html
 *    hideHeader:           (optional) If true, hides the panel header
 *    hideControls:         (optional) If true, hides header controls
 *    allowAddToDashboard:  (optional) If true, shows the add to dashboard control '+'
 * }
 **/
function PanelTemplate(options) {
    this.panelType = options.panelType;
    this.widgetType = options.widgetType;
    this.title = options.title;
    this.width = options.width;
    this.height = options.height;
    this.header = options.header;
    this.footer = options.footer;
    this.widgetImage = options.image;
    this.widgetImageDark = options.imageDark ||options.image;
    this.description = options.description;
    this.maxCount = options.maxCount;
    this.panelSettings = options.panelSettings;
    this.optionsComp = options.optionsComp;
    this.defaultOptions = options.defaultOptions;
    this.dataSources = options.dataSources || [];
    this.dataFormatter = options.dataFormatter;
    this.fixedPanel = (options.fixedPanel === null) ? false : options.fixedPanel;
    this.optionsLink = options.optionsLink;
    this.hideHeader = options.hideHeader;
    this.hideControls = options.hideControls;
    this.allowAddToDashboard = options.allowAddToDashboard;
    this.allowedSystems = options.allowedSystems;
    this.showInnerGrid = options.showInnerGrid;
}

function PanelTemplateFactory() {
    var self = this;

    self.dataProvider = DataProvider.getInstance();
    self.dataRepository = DataRepository.getInstance();
    self.panelsMasterList = [];

    self.defaults = {
        historical2: {
            panelOptions: {
                pageSize: 25,
                resize: false,
                pageNum: 1,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: []
            },
            gridColumns: {
                MeetingXml: ["dimmeeting.meetingname", "dimsystem.systemname", "fctmeeting.starttime", "fctmeeting.meetingminute", "fctmeeting.meetingparticipantcount", "dimqualityclass.qualityclass"],
                VmrXml: ["dimvmr.vmrname", "dimsystem.systemname", "dimvmr.meetingcount", "dimvmr.meetingparticipantminute", "dimvmr.meetingparticipantminuteaudio", "dimvmr.meetingparticipantminutevideo", "dimvmr.meetingparticipantcount", "dimvmr.uniqueparticipantcount"],
                ParticipantXml: ["dimparticipant.participantaddress", "dimparticipant.participantdisplayname", "dimmeeting.meetingname", "fctparticipant.starttime", "fctparticipant.participantminute", "dimprotocol.protocol", "dimmodality.modality", "dimqualityclass.qualityclass"],
                ManageEndpointXml: ["dimendpoint.endpointname", "dimendpoint.endpointsubcategory", "dimendpoint.model", "fctmanageendpoint.callcount", "fctmanageendpoint.callminute", "manageendpointxml.utilization", "fctmanageendpoint.failcallcount", "fctmanageendpoint.badqualitycallcount"],
                UnmanageEndpointXml: ["dimunmanageendpoint.endpointname", "dimunmanageendpoint.endpointtype", "fctunmanageendpoint.callcount", "fctunmanageendpoint.callminute", "fctunmanageendpoint.failcallcount", "fctunmanageendpoint.badqualitycallcount"],
                CallXml: ["dimcall.sourcecalleraddress", "dimcall.targetcalleraddress", "fctcall.starttime", "dimcallstatus.callstatus", "fctcall.callminute", "dimcall.callbandwidth", "dimmodality.modality", "dimcallreason.callreason", "fctcall.packetlossrate"],
            }
        }
    };

    self.panelsMasterList.push(
        // REAL TIME ALERTS
        self.getAlertsTemplate(),
        // WEBEX
        self.getWebexMeetingsDetailsSearchGridTemplate(),
        self.getWebexMeetingsFlexQueryTemplate(),
        self.getWebexMeetingsSummaryKpiMulticellTemplate(),
        self.getWebexUsersDetailsSearchGridTemplate(),
        self.getWebexUsersFlexQueryTemplate(),
        self.getWebexUsersSummaryKpiMulticellTemplate(),
        self.getWebexParticipantsDetailsSearchGridTemplate(),
        self.getWebexParticipantsFlexQueryTemplate(),
        // WEBEX REPORTS
        self.getWebexSchedulesTemplate(),
        // HISTORICAL REPORTS
        self.getHistoricalSchedulesTemplate(),
        self.getHistorical2SchedulesTemplate(),
        self.getHistoricalMeetingParticipantDetailsSearchGridTemplate(),
        self.getHistoricalMeetingParticipantSummaryMulticellTemplate(),
        self.getHistoricalMeetingVMRDetailsSearchGridTemplate(),
        self.getHistoricalMeetingVMRSummaryMulticellTemplate(),
        self.getHistoricalMeetingParticipantFlexQueryTemplate(),
        self.getHistoricalMeetingVMRFlexQueryTemplate(),
        self.getHistoricalMeetingsSummaryKpiMulticellTemplate(),
        self.getHistoricalMeetingsDetailsSearchGridTemplate(),
        self.getHistoricalMeetingsFlexQueryTemplate(),
        self.getHistoricalInfrastructureXmlTemplate(),
        self.getHistoricalInfrastructureMetadataXmlTemplate(),
        self.getHistoricalNetworksXmlTemplate(),
        self.getHistoricalNetworksMetadataXmlTemplate(),
        self.getHistoricalTrunksXmlTemplate(),
        self.getHistoricalTrunksMetadataXmlTemplate(),
        self.getHistoricalUserXmlTemplate(),
        self.getHistoricalUserMetadataXmlTemplate(),
        self.getHistoricalManagedEndpointsDetailsSearchGridTemplate(),
        self.getHistoricalManagedEndpointsFlexQueryTemplate(),
        self.getHistoricalManagedEndpointsSummaryKpiMulticellTemplate(),
        self.getHistoricalUnmanagedEndpointDetailsSearchGridTemplate(),
        self.getHistoricalUnmanagedEndpointFlexQueryTemplate(),
        self.getHistoricalUnmanagedEndpointSummaryMulticellTemplate(),
        self.getHistoricalEndpointInventoryDetailsSearchGridTemplate(),
        self.getHistoricalEndpointInventoryFlexQueryTemplate(),
        self.getHistoricalIssuesDetailsSearchGridTemplate(),
        self.getHistoricalIssuesFlexQueryTemplate(),
        // LYNC
        self.getLyncUsersDetailsSearchGridTemplate(),
        self.getLyncUsersFlexQueryTemplate(),
        self.getLyncUsersSummaryKpiMulticellTemplate(),
        // LYNC REPORTS
        self.getLyncSchedulesTemplate(),
        // ENDPOINT DETAILS
        self.getEndpointAVInfoTemplate(),
        self.getEndpointCodecInfoTemplate(),
        self.getEndpointNetworkInfoTemplate(),
        self.getEndpointInfoActiveCallsTemplate(),
        self.getEndpointInfoHistoricalCallsTemplate(),
        // SETTINGS
        self.getEndpointManagementGridTemplate(),
        self.getRoomGridTemplate(),
        // HISTORICAL 2
        self.getDistributionFlexiPanelTemplate(),
        self.getTrendFlexiPanelTemplate(),
        self.getSummaryFlexiPanelTemplate(),
        self.getSummaryPanelTemplate(),
        self.getKpiCellPanelTemplate(),
        self.getKPIPanelTemplate(),
        self.getHistoricalEndpointCallMetadataXmlTemplate(),
        self.getHistoricalEndpointCallXmlTemplate(),
        self.getHistoricalMeetingXmlTemplate(),
        self.getHistoricalMeetingMetadataXmlTemplate(),
        self.getHistoricalCapacityDetailsTrendTemplate(),
        self.getHistoricalCapacityDetailsParticipantsSearchGridTemplate(),
        self.getHistoricalClusterCapacityDetailsTrendTemplate(),
        self.getHistoricalClusterCapacityDetailsParticipantsSearchGridTemplate(),
        self.getHistoricalTrunkCapacityDetailsTrendTemplate(),
        self.getHistoricalTrunkCapacityDetailsParticipantsSearchGridTemplate(),
        self.getConcurrentCallsCapacityDetailsTrendTemplate(),
        self.getConcurrentCallsCapacityDetailsParticipantsSearchGridTemplate(),
        self.getConcurrentBandwidthCapacityDetailsTrendTemplate(),
        self.getConcurrentBandwidthCapacityDetailsParticipantsSearchGridTemplate(),
        self.getSysConcurrentMeetingPeakBySysTypeCapacityDetailsTrendTemplate(),
        self.getSysConcurrentMeetingPeakBySysTypeCapacityDetailsTableTemplate(),
        self.getSysConcurrentMeetingPeakBySysNameCapacityDetailsTrendTemplate(),
        self.getSysConcurrentMeetingPeakBySysNameCapacityDetailsTableTemplate(),
        self.getSysTypeConcurrentMeetingPeakCapacityDetailsTrendTemplate(),
        self.getSysTypeConcurrentMeetingPeakCapacityDetailsTableTemplate(),
        self.getHistoricalManagedEndpointXmlTemplate(),
        self.getHistoricalManagedEndpointMetadataXmlTemplate(),
        self.getHistoricalEndpointInventoryXmlTemplate(),
        self.getHistoricalEndpointInventoryMetadataXmlTemplate(),
        self.getHistoricalUnmanagedEndpointXmlTemplate(),
        self.getHistoricalUnmanagedEndpointMetadataXmlTemplate(),
        self.getHistoricalMeetingParticipantsXmlTemplate(),
        self.getHistoricalMeetingParticipantsMetadataXmlTemplate(),
        self.getHistoricalMeetingVmrXmlTemplate(),
        self.getHistoricalMeetingVmrMetadataXmlTemplate(),
        self.getHistoricalRoomsTemplate(),
        self.getHistoricalRoomsMetadataTemplate(),
        self.getHistoricalBookingsTemplate(),
        self.getHistoricalBookingsMetadataTemplate()
    );

    self.filteredPanelsList = {};
    self.getFilteredPanelsListPromise();
}

PanelTemplateFactory.prototype = {

    getFilteredPanelsListPromise: function () {
        var self = this;
        var filteredPanelsList = ko.unwrap(self.filteredPanelsList);

        if (_.isEmpty(filteredPanelsList)) {
            return getFilteredPanelsListBasePromise();

        } else {
            return Promise.resolve(filteredPanelsList);
        }

        function getFilteredPanelsListBasePromise() {
            var panelsNameFilteredList = getPanelsNameFullList();

            //  this method is a 'thenable'
            return self.dataProvider.loadSystems()
            //  Note that it was needed to wrap this into another 'Deferred' object
            //   to be able to return 'always' result
            //   and make 'getFilteredPanelsListPromise' also promise-chainable.

            // UI-1751 update (Q1 2021): If we rely on loadSystems completing successfully, we should use "then" instead of "always"
            //                           (or in the case of ES6 promises, "finally"). If loadSystems fails, we should not proceed.
                .then(function () {
                    //  This call asking for a new provider must be placed here
                    //   since it has a closure over the 'getDictionaryOfSystems' method (from 'dataRepository')
                    //   it heavily depends on previous promise resolution (dataProvider.loadSystemDictionary()).
                    var isPanelAllowed = isPanelAllowedProvider("allowedSystems");

                    self.filteredPanelsList = {};

                    var keys = Object.keys(panelsNameFilteredList);
                    keys.forEach(function (key) {
                        self.filteredPanelsList[key] = _
                            .chain(panelsNameFilteredList[key])
                            .map(function (panelName) {
                                return self.getPanelFromType(panelName);
                            })
                            .filter(isPanelAllowed)
                            .value();
                    });

                    return self.filteredPanelsList;
                })
                .catch(function(error) {
                  throw new Error('Error loading systems list: ' + error.message);
                });

            function doesCollectionsIntersect(firstCollection, secondCollection, partially) {
                var result = false;

                if (!_.isNil(firstCollection) && !_.isNil(secondCollection)) {
                    partially = _.isNil(partially) ? true : Boolean(partially);
                    var reducerFn = (partially) ? partialIntersection : completeIntersection;
                    result = _.reduce(secondCollection, reducerFn, !partially);
                }

                return result;

                function completeIntersection(result, value) {
                    return result && _.includes(firstCollection, value);
                }

                function partialIntersection(result, value) {
                    return result || _.includes(firstCollection, value);
                }
            }

            function isPanelAllowedProvider(key) {
                var dictionaryOfSystems = self.dataRepository.getDictionaryOfSystems();
                var keysInDictionaryOfSystems = _.keysIn(dictionaryOfSystems);
                return function isPanelAllowed(allowedSystemsArg) {
                    var allowedSystems = _.isNil(key) ? allowedSystemsArg : _.get(allowedSystemsArg, key);
                    return (_.isNil(allowedSystems) ||
                        doesCollectionsIntersect(keysInDictionaryOfSystems, allowedSystems));
                };
            }
        }
    },

    getPanelFromType: function (paneltype) {
        for (var i = 0; i < this.panelsMasterList.length; i++) {
            var panelItem = this.panelsMasterList[i];
            if (panelItem.panelType === paneltype) {
                return panelItem;
            }
        }
        return null;
    },

    dispose: function () {
    },

    getBridgeLicenseUtilizationGaugePanelTemplate: function () {
        return new PanelTemplate({
            panelType: "BridgeLicenseUtilizationGauge",
            widgetType: "gauge-widget",
            title: "Bridge License Utilization",
            width: 1,
            height: 5,
            header: "Bridge License",
            footer: "Utilization",
            image: "BridgeLicenseUtilizationGauge.png",
            description: "This panel displays the percentage of licenses utilized for bridges.",
            maxCount: 2,
            optionsComp: "gauge-options",
            defaultOptions: {
                optionHeader: "Bridge License Options",
                panelHeader: "Bridge License"
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/gauge-formatter", params: { type: "Bridge" } }
        });
    },

    getCallControlLicenseUtilizationGaugePanelTemplate: function () {
        return new PanelTemplate({
            panelType: "CallControlLicenseUtilizationGauge",
            widgetType: "gauge-widget",
            title: "Call Control License Utilization",
            width: 1,
            height: 5,
            header: "Call Control License",
            footer: "Utilization",
            image: "CallControlLicenseUtilizationGauge.png",
            description: "This panel displays the percentage of licenses utilized for call control devices.",
            maxCount: 2,
            optionsComp: "gauge-options",
            defaultOptions: {
                optionHeader: "Call Control Options",
                panelHeader: "Call Control License"
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/gauge-formatter", params: { type: "CallControl" } }
        });
    },

    getLicenseUtilizationLineChartPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "LicenseUtilizationLineChart",
            widgetType: "graph-widget",
            title: "License Utilization - Bridge & Call Control",
            width: 2,
            height: 5,
            header: "License Utilization",
            image: "LicenseUtilizationLineChart.png",
            description: "This panel displays the license utilization status of bridge and call control devices.",
            maxCount: 1,
            optionsComp: "licensegraph-options",
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/licenselinechart-formatter", params: {} }
        });
    },

    getEndPointHwSummaryTwoCellPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "EndPointHwSummaryTwoCell",
            widgetType: "twocell-widget",
            title: "Endpoint Hardware Issues Summary",
            width: 1,
            height: 5,
            header: "Endpoint Hardware Issues Summary",
            image: "EndpointStatusUpDownTwoCell.png",
            description: "This panel helps you track the number of available and in-call endpoints that have camera or display disconnected.",
            maxCount: 1,
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/twocell-formatter", params: { type: "EndpointHwSummary" } }
        });
    },

    getEndpointStatusUpDownTwoCellPanelTemplate: function (original) {
        let panelType = original ? "EndPointStatusUpDownTwoCell" : "EndpointStatusUpDownTwoCell";
        return new PanelTemplate({
            panelType: panelType,
            widgetType: "twocell-widget",
            title: "Endpoint Status - Up/Down",
            width: 1,
            height: 5,
            header: "Endpoint Status",
            image: "EndpointStatusUpDownTwoCell.png",
            description: "This panel helps you track the number of end points that are out of commission.",
            maxCount: 1,
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/twocell-formatter", params: { type: "Endpoint" } }
        });
    },

    getBridgeStatusUpDownTwoCellPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "BridgeStatusUpDownTwoCell",
            widgetType: "twocell-widget",
            title: "Bridge Status - Up/Down",
            width: 1,
            height: 5,
            header: "Bridge Status",
            image: "BridgeStatusUpDownTwoCell.png",
            description: "This panel helps you track the number of bridges that are out of commission.",
            maxCount: 1,
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/twocell-formatter", params: { type: "Bridge" } }
        });
    },

    getCallControlStatusUpDownTwoCellPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "CallControlStatusUpDownTwoCell",
            widgetType: "twocell-widget",
            title: "Call Control Status - Up/Down",
            width: 1,
            height: 5,
            header: "Call Control Status",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "This panel helps you track the number of call control devices that are out of commission.",
            maxCount: 1,
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/twocell-formatter", params: { type: "CallControl" } }
        });
    },

    getEndpointStatusKoGridPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointStatusKoGrid",
            widgetType: "grid-widget",
            title: "Status",
            width: 2,
            height: 8,
            header: "Endpoint Status",
            image: "EndpointStatusGrid.png",
            description: "The panel provides real-time Endpoint status.",
            maxCount: 100,
            optionsComp: "endpointstatus-options",
            defaultOptions: {
                resize: true,
                sortIndex: 0,
                pageNum: 1,
                pageSize: 10,
                sortAsc: true
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointstatusgrid-formatter", params: {} }
        });
    },

    getEndpointStatusMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointStatusMulticell",
            widgetType: "multicell-widget",
            title: "Endpoint Status",
            width: 12,
            height: 5,
            header: "Endpoint Status",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides real-time Endpoint status.",
            maxCount: 1,
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetSummaryXml",
                resType: "xml",
                refreshInterval: 15,
                params: {}
            },
                {
                    service: "WebMethods.aspx",
                    method: "GetEndpointsJson",
                    resType: "json",
                    refreshInterval: 0,
                    params: {}
                }],
            dataFormatter: { name: "src/app/data-formatters/multicell-formatter", params: { type: "Endpoint" } },
            fixedPanel: true
        });
    },

    // NOTE: For all quality panels but the last one (quailtytitle), we commented out all data source specs
    //   because this was leading to superfluous service calls for the same data.
    //   Since formatters are all subscribed to the same data it's enough for all others if only one panel does the request.

    getCallQualityHeaderGridPanelTemplate: function (endpointsPrefix) {
        return new PanelTemplate({
            panelType: EmptyIfNull(endpointsPrefix) + 'CallQualityHeaderGrid',
            widgetType: 'grid-widget',
            title: 'Call Quality Header',
            width: 12,
            height: 2,
            image: 'mcustatuskogrid.png',
            description: 'The panel is header on call quality page',
            maxCount: 1,
            defaultOptions: EndpointsOption(endpointsPrefix),
            dataSources: [],
            dataFormatter: { name: 'src/app/data-formatters/qualitygrids-formatter', params: { type: 'Header' } }
        });
    },

    getEndpointStatusSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointStatusSearchGrid",
            widgetType: "searchgrid-widget",
            title: "Endpoint Status",
            width: 2,
            height: 2,
            image: "acanostatuskogrid.png",
            description: "The panel provides real-time Endpoint status.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointstatussearchgrid-formatter", params: {} },
            fixedPanel: true
        });
    },

///////////////////////////////////////////////////////////////////////////////
// Start Settings panels
///////////////////////////////////////////////////////////////////////////////
    getEndpointManagementGridTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointManagementGrid",
            widgetType: "endpointmgmtgrid-widget",
            title: "Endpoint Status",
            width: 2,
            height: 2,
            image: "acanostatuskogrid.png",
            description: "The panel provides real-time Endpoint status.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 0,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointmgmtgrid-formatter", params: {} },
            fixedPanel: true
        });
    },

    getRoomGridTemplate: function () {
        return new PanelTemplate({
            panelType: "RoomGrid",
            widgetType: "roomgrid-widget",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 6,
                sortAsc: true
            },
            dataSources: [
                {
                    service: "WebMethods.aspx",
                    method: "GetRooms",
                    resType: "json",
                    refreshInterval: 0,
                    params: {}
                },
                {
                    service: "WebMethods.aspx",
                    method: "GetEndpointsJson",
                    resType: "json",
                    refreshInterval: 0,
                    params: {}
                }
            ],
            dataFormatter: { name: "src/app/data-formatters/roomgrid-formatter", params: {} },
            fixedPanel: true
        });
    },

    getEndpointCodecInfoTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointCodecInfo",
            widgetType: "endpointcodecinfo-widget",
            title: "Endpoint Codec Info",
            width: 2,
            height: 5,
            header: "Endpoint Codec Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Endpoint Codec Details.",
            maxCount: 1,
            defaultOptions: {},
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointdetail-formatter", params: { type: "Codec" } },
            fixedPanel: true
        });
    },

    ///////////////////////////////////////////////////////////////////////////////
    // Start Endpoint Details panels
    ///////////////////////////////////////////////////////////////////////////////
    getEndpointAVInfoTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointAVInfo",
            widgetType: "endpointavinfo-widget",
            title: "Endpoint AV Info",
            width: 2,
            height: 5,
            header: "Endpoint AV Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Endpoint AV Details.",
            maxCount: 1,
            defaultOptions: {},
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointdetail-formatter", params: { type: "Codec" } },
            fixedPanel: true
        });
    },

    getEndpointInfoActiveCallsTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointInfoActiveCalls",
            widgetType: "endpointactivecalls-widget",
            title: "Endpoint Call Info",
            width: 2,
            height: 5,
            header: "Endpoint Call Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Endpoint Call Details.",
            maxCount: 1,
            defaultOptions: {},
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointdetail-formatter", params: { type: "ActiveCalls" } },
            fixedPanel: true
        });
    },

    getEndpointInfoHistoricalCallsTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointInfoHistoricalCalls",
            widgetType: "endpointhistoricalcalls-widget",
            title: "Historical Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 3,
                sortAsc: false,
                columnOptions: [],
                dataset: "CallXml",
                display: [
                    "dimcall.sourcecalleraddress",
                    "dimcall.targetcalleraddress",
                    "fctcall.starttime",
                    "fctcall.endtime",
                    "dimcall.callbandwidth",
                    "dimmodality.modality",
                    "fctcall.callminute",
                    "dimcallstatus.callstatus",
                    "dimcallreason.callreason",
                    "dimpacketlossclass.packetlossclass",
                    "fctcall.packetlossrate",
                    "dimqualityclass.qualityclass",
                    "dimcallroute.callroute",
                    "dimprotocol.protocol"
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getEndpointNetworkInfoTemplate: function () {
        return new PanelTemplate({
            panelType: "EndpointNetworkInfo",
            widgetType: "endpointnetworkinfo-widget",
            title: "Endpoint Network Info",
            width: 2,
            height: 5,
            header: "Endpoint Network Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Endpoint Network Details.",
            maxCount: 1,
            defaultOptions: {},
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetEndpointsJson",
                resType: "json",
                refreshInterval: 15,
                params: {}
            }],
            dataFormatter: { name: "src/app/data-formatters/endpointdetail-formatter", params: { type: "Codec" } },
            fixedPanel: true
        });
    },

    getWebexSchedulesTemplate: function () {
        return new PanelTemplate({
            panelType: "webexSchedules",
            widgetType: "schedulegrid-widget",
            title: "Schedules",
            width: 2,
            height: 5,
            header: "Schedules",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides WebEx Scheduled reports.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                    service: "ExportRegistrations.aspx",
                    method: "ListExportRegistrations",
                resType: "json",
                refreshInterval: 60,
                    params: { observableParams: true, obsParamsInitValue: { filter: "webex" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/schedulegrid-formatter", params: { type: 'webex' } },
            fixedPanel: true
        });
    },

    getHistoricalSchedulesTemplate: function () {
        return new PanelTemplate({
            panelType: "historicalSchedules",
            widgetType: "schedulegrid-widget",
            title: "Schedules",
            width: 2,
            height: 5,
            header: "Schedules",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides WebEx Scheduled reports.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                    service: "ExportRegistrations.aspx",
                    method: "ListExportRegistrations",
                resType: "json",
                refreshInterval: 60,
                    params: { observableParams: true, obsParamsInitValue: { filter: "historical" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/schedulegrid-formatter", params: { type: 'historical' } },
            fixedPanel: true
        });
    },

    getHistorical2SchedulesTemplate: function () {
        return new PanelTemplate({
            panelType: "historical2Schedules",
            widgetType: "schedulegrid-widget",
            title: "Schedules",
            width: 2,
            height: 5,
            header: "Schedules",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides WebEx Scheduled reports.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                    service: "ExportRegistrations.aspx",
                    method: "ListExportRegistrations",
                resType: "json",
                refreshInterval: 60,
                    params: { observableParams: true, obsParamsInitValue: { updateHack: "hack" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/schedulegrid-formatter", params: {} },
            fixedPanel: true
        });
    },

    getLyncSchedulesTemplate: function () {
        return new PanelTemplate({
            panelType: "lyncSchedules",
            widgetType: "schedulegrid-widget",
            title: "Schedules",
            width: 2,
            height: 5,
            header: "Schedules",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Lync Scheduled reports.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                    service: "ExportRegistrations.aspx",
                    method: "ListExportRegistrations",
                resType: "json",
                refreshInterval: 60,
                    params: { observableParams: true, obsParamsInitValue: { filter: "lync" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/schedulegrid-formatter", params: { type: 'lync' } },
            fixedPanel: true
        });
    },

///////////////////////////////////////////////////////////////////////////////
// Start Webex panels
///////////////////////////////////////////////////////////////////////////////

    ////////////////////// LYNC USERS //////////////////////

    ////////////USERS TAB/////////////

    getLyncUsersSummaryKpiMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "LyncUsersSummary",
            widgetType: "kpimulticell-widget",
            title: "Lync User Stats",
            width: 12,
            height: 1,
            header: "Lync User Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Lync User Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalUsers', title: 'Total Users', format: formatNumericValueInt0Default },
                    { name: 'TotalASSession', title: 'App. Sharing Sessions', format: formatNumericValueInt0Default },
                    {
                        name: 'TotalASDurationMin',
                        title: 'App. Sharing Mins',
                        format:
                        formatNumericValueWithSuffix0Default
                    },
                    { name: 'TotalAudioSession', title: 'Audio Sessions', format: formatNumericValueInt0Default },
                    {
                        name: 'TotalAudioDurationMin',
                        title: 'Audio Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    { name: 'TotalFTSession', title: 'File Transfer Sessions', format: formatNumericValueInt0Default },
                    {
                        name: 'TotalFTDurationMin',
                        title: 'File Transfer Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    { name: 'TotalIMSession', title: 'IM Sessions', format: formatNumericValueInt0Default },
                    { name: 'TotalIMDurationMin', title: 'IM Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalVideoSession', title: 'Video Sessions', format: formatNumericValueInt0Default },
                    { name: 'TotalVideoDurationMin', title: 'Video Mins', format: formatNumericValueWithSuffix0Default }
                ],
                display: [
                    'TotalUsers', 'TotalASSession', 'TotalASDurationMin', 'TotalAudioSession', 'TotalAudioDurationMin',
                    'TotalFTSession', 'TotalFTDurationMin', 'TotalIMSession', 'TotalIMDurationMin', 'TotalVideoSession',
                    'TotalVideoDurationMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "LyncUserSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getLyncUsersDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "LyncUsersDetails",
            widgetType: "searchgrid2-widget",
            title: "Lync User Details",
            width: 2,
            height: 5,
            header: "Lync User Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Lync User Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: false,
                columnOptions: [
                    { name: 'sourceCallerSID', title: 'SourceCallerSID', idCol: true, hideFromSelectList: true },
                    { name: 'systemName', title: 'User', widthRatio: 2, format: formatLink },
                    { name: 'sessionCount_appShare', title: 'App. Sharing Sessions', format: formatNumericValueInt },
                    { name: 'sessionMinute_appShare', title: 'App. Sharing Mins', format: formatNumericValueInt },
                    { name: 'sessionCount_audio', title: 'Audio Sessions', format: formatNumericValueInt },
                    { name: 'sessionMinute_audio', title: 'Audio Mins', format: formatNumericValueInt },
                    {
                        name: 'sessionCount_fileTransfer',
                        title: 'File Transfer Sessions',
                        format: formatNumericValueInt
                    },
                    { name: 'sessionMinute_fileTransfer', title: 'File Transfer Mins', format: formatNumericValueInt },
                    { name: 'sessionCount_IM', title: 'IM Sessions', format: formatNumericValueInt },
                    { name: 'sessionMinute_IM', title: 'IM Mins', format: formatNumericValueInt },
                    { name: 'sessionCount_video', title: 'Video Sessions', format: formatNumericValueInt },
                    { name: 'sessionMinute_video', title: 'Video Mins', format: formatNumericValueInt },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: 'TargetCallerAddress', title: 'Target User', widthRatio: 2 },
                                { name: 'sessionType', title: 'Session Type' },
                                { name: 'StartTime', title: 'Start Time', format: formatDate },
                                { name: 'EndTime', title: 'End Time', format: formatDate },
                                {
                                    name: 'sessionMinute',
                                    title: 'Duration (Min)',
                                    format: formatNumericValueWithSuffix0Default
                                }
                            ],

                        display: ["TargetCallerAddress", "sessionType", "StartTime", "EndTime", "sessionMinute"],
                        sortColumn: 'sessionType desc',
                        queryId: 'LyncUserSessionsDetails',
                        parentFkColumns: [{ name: 'sourceCallerSID', type: 'numeric' }]
                    }
                ],
                display: [
                    'systemName', 'sessionCount_appShare', 'sessionMinute_appShare', 'sessionCount_audio',
                    'sessionMinute_audio', 'sessionCount_fileTransfer', 'sessionMinute_fileTransfer', 'sessionCount_IM',
                    'sessionMinute_IM', 'sessionCount_video', 'sessionMinute_video'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "LyncUserDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getLyncUsersFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "LyncUsersFlexQuery",
            widgetType: "flexquery-widget",
            title: "Lync Users Details",
            width: 2,
            height: 5,
            header: "Lync User Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Lync User Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'systemName', title: 'User', type: 'string' },
                    { name: 'sessionCount_appShare', title: 'App. Sharing Sessions', type: 'integer' },
                    { name: 'sessionMinute_appShare', title: 'App. Sharing Mins', type: 'integer' },
                    { name: 'sessionCount_audio', title: 'Audio Sessions', type: 'integer' },
                    { name: 'sessionMinute_audio', title: 'Audio Mins', type: 'integer' },
                    { name: 'sessionCount_fileTransfer', title: 'File Transfer Sessions', type: 'integer' },
                    { name: 'sessionMinute_fileTransfer', title: 'File Transfer Mins', type: 'integer' },
                    { name: 'sessionCount_IM', title: 'IM Sessions', type: 'integer' },
                    { name: 'sessionMinute_IM', title: 'IM Mins', type: 'integer' },
                    { name: 'sessionCount_video', title: 'Video Sessions', type: 'integer' },
                    { name: 'sessionMinute_video', title: 'Video Mins', type: 'integer' }
                ]
            },
            fixedPanel: true
        });
    },

    ////////////////////// ALERTS //////////////////////

    getAlertsTemplate: function () {
        return new PanelTemplate({
            panelType: "Alerts",
            widgetType: "alertgrid-widget",
            title: "Alerts",
            width: 2,
            height: 5,
            header: "Alerts",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides WebEx Meeting Stats.",
            maxCount: 1,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true
            },
            dataSources: [{
                service: "WebMethods.aspx",
                method: "GetOrgPreferences",
                resType: "json",
                refreshInterval: 60,
                params: { observableParams: true, obsParamsInitValue: { key: "vAnalyticsOrgPreferences" } }
            },
                {
                    service: "DatabaseMethods.aspx",
                    method: "GetSystemsJson",
                    resType: "json",
                    refreshInterval: 0,
                    params: { typelist: REALTIME_SYSTEM_TYPES }
                },
                {
                    service: "WebMethods.aspx",
                    method: "GetEndpointsJson",
                    resType: "json",
                    refreshInterval: 0,
                    params: {}
                }],
            dataFormatter: { name: "src/app/data-formatters/alertgrid-formatter", params: {} },
            fixedPanel: true
        });
    },

    ////////////////////// WEBEX MEETINGS //////////////////////

    getWebexMeetingsSummaryKpiMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexMeetingsSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical Participant Stats",
            width: 12,
            height: 1,
            header: "Historical Participant Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Summary Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'Meetings', title: 'Meetings', format: formatNumericValueInt0Default },
                    { name: 'DurationMin', title: 'Meeting Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'Participants', title: 'Participants', format: formatNumericValueInt0Default },
                    { name: 'PeopleMin', title: 'Participant Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'CallInMin', title: 'Toll Call In Mins', format: formatNumericValueWithSuffix0Default },
                    {
                        name: 'CallOutDomesticMin',
                        title: 'Call Out Dom. Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'CallOutInternationalMin',
                        title: 'Call Out Intl. Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    { name: 'TollFreeMin', title: 'Toll Free Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'VoipMin', title: 'VOIP Mins', format: formatNumericValueWithSuffix0Default }
                ],
                display: [
                    'Meetings', 'DurationMin', 'Participants', 'PeopleMin', 'CallInMin', 'CallOutDomesticMin',
                    'CallOutInternationalMin', 'TollFreeMin', 'VoipMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalMeetingParticipantSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getWebexMeetingsDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexMeetingsDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    {
                        name: 'ftWebexMeetingActivitySID',
                        title: 'ftWebexMeetingActivitySID',
                        idCol: true,
                        hideFromSelectList: true
                    },
                    { name: 'ConferenceName', title: 'Meeting', widthRatio: 2, format: formatLink },
                    { name: 'StartTime', title: 'Start Time', widthRatio: 1.25, format: formatDate },
                    { name: 'TelepresenceMeeting', title: 'CMR Meeting' },
                    { name: 'HostName', title: 'Host' },
                    { name: 'DurationMin', title: 'Meeting Mins', format: formatNumericValueInt },
                    { name: 'ParticipantTelepresenceMin', title: 'CMR Mins', format: formatNumericValueInt },
                    { name: 'ParticipantCount', title: 'Participants', format: formatNumericValueInt },
                    { name: 'PeopleMin', title: 'Participant Mins', format: formatNumericValueInt },
                    { name: 'CallInMin', title: 'Toll Call In Mins', format: formatNumericValueInt },
                    { name: 'CallOutDomesticMin', title: 'Call Out Dom. Mins', format: formatNumericValueInt },
                    { name: 'CallOutInternationalMin', title: 'Call Out Intl. Mins', format: formatNumericValueInt },
                    { name: 'TollfreeMin', title: 'Toll Free Mins', format: formatNumericValueInt },
                    { name: 'VoipMin', title: 'VOIP Mins', format: formatNumericValueInt },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "ParticipantName", title: 'Participant' },
                                { name: "JoinTime", title: 'Join Time', format: formatDate },
                                { name: "Region", title: 'Region' },
                                { name: "CountryName", title: 'Country' },
                                { name: "DurationMin", title: 'Duration (Mins)', format: formatNumericValueInt },
                                { name: "Type", title: 'User Type' },
                                { name: "CMRParticipant", title: 'CMR Participant' },
                                { name: "ID", title: 'ID' }
                            ],
                        display: [
                            "ParticipantName", "JoinTime", "Region", "CountryName", "DurationMin", "Type", "CMRParticipant"
                        ],
                        sortColumn: 'JoinAsHostFlag desc, durationmin desc',
                        queryId: 'WebexMeetingsInnerDetails',
                        parentFkColumns: [{ name: 'ftWebexMeetingActivitySID', type: 'numeric' }]
                    }
                ],
                display: [
                    'ConferenceName', 'StartTime', 'HostName', 'TelepresenceMeeting', 'DurationMin', 'ParticipantCount',
                    'PeopleMin', 'CallInMin', 'CallOutDomesticMin', 'CallOutInternationalMin', 'TollfreeMin', 'VoipMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getWebexMeetingsFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexMeetingsFlexQuery",
            widgetType: "flexquery-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'ConferenceName', title: 'Meeting', type: 'string' },
                    { name: 'StartTime', title: 'Start Time', type: 'datetime' },
                    { name: 'HostName', title: 'Host', type: 'string' },
                    { name: 'TelepresenceMeeting', title: 'CMR Meeting', type: 'string' },
                    { name: 'durationmin', title: 'Meeting Mins', type: 'integer' },
                    { name: 'participanttelepresencemin', title: 'CMR Mins', type: 'integer' },
                    { name: 'participantcount', title: 'Participants', type: 'integer' },
                    { name: 'peoplemin', title: 'Participant Mins', type: 'integer' },
                    { name: 'CallInMin', title: 'Toll Call In Mins', type: 'integer' },
                    { name: 'CallOutDomesticMin', title: 'Call Out Dom. Mins', type: 'integer' },
                    { name: 'CallOutInternationalMin', title: 'Call Out Intl. Mins', type: 'integer' },
                    { name: 'TollfreeMin', title: 'Toll Free Mins', type: 'integer' },
                    { name: 'VoipMin', title: 'Voip Mins', type: 'integer' }
                ]
            },
            fixedPanel: true
        });
    },

    ////////////////////// WEBEX USERS //////////////////////

    getWebexUsersSummaryKpiMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexUsersSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical Participant Stats",
            width: 12,
            height: 1,
            header: "Historical Participant Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Summary Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    {
                        name: 'ActiveHost',
                        title: 'Active Hosts',
                        format: formatNumericValueInt0Default
                    },
                    {
                        name: 'Hosts',
                        title: 'Hosts',
                        format: formatNumericValueInt0Default
                    },
                    {
                        name: 'Meetings',
                        title: 'Meetings',
                        format: formatNumericValueInt0Default
                    },
                    {
                        name: 'TPMeetings',
                        title: 'CMR Meetings',
                        format: formatNumericValueInt0Default
                    },
                    {
                        name: 'DurationMin',
                        title: 'Meeting Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'PeopleMin',
                        title: 'Participant Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'CallInMin',
                        title: 'Total Call In Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'CallOutMin',
                        title: 'Call Out Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'TollFreeMin',
                        title: 'Toll Free Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'VoipMin',
                        title: 'VOIP Mins',
                        format: formatNumericValueWithSuffix0Default
                    }
                ],
                display: [
                    'ActiveHost', 'Hosts', 'Meetings', 'TPMeetings', 'DurationMin', 'PeopleMin', 'CallInMin', 'CallOutMin',
                    'TollFreeMin', 'VoipMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalMeetingParticipantSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getWebexUsersDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexUsersDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 3,
                sortAsc: false,
                columnOptions: [
                    { name: 'HostdnUserSID', title: 'HostdnUserSID', idCol: true, hideFromSelectList: true },
                    { name: 'DisplayName', title: 'Host', widthRatio: 2, format: formatLink },
                    { name: 'MeetingCount', title: 'Meetings', format: formatNumericValueInt },
                    { name: 'TelepresenceMeetingCount', title: 'CMR Meetings', format: formatNumericValueInt },
                    { name: 'TotalDuration', title: 'Meeting Mins', format: formatNumericValueInt },
                    { name: 'TotalParticipantCount', title: 'Participants', format: formatNumericValueInt },
                    { name: 'TotalPeopleMin', title: 'Participant Mins', format: formatNumericValueInt },
                    {
                        name: 'TotalCallInMin',
                        title: 'Total Call In Mins',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'TotalCallOutDomesticMin',
                        title: 'Call Out Dom. Mins',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'TotalCallOutInternationalMin',
                        title: 'Call Out Intl. Mins',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'TotalTollFreeMin',
                        title: 'Toll Free Mins',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'TotalVoipMin',
                        title: 'VOIP Mins',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "ConferenceName", title: 'Meeting Name', widthRatio: 2 },
                                { name: "StartTime", title: 'Start Time', format: formatDate, widthRatio: 1.5 },
                                { name: "TelepresenceMeeting", title: 'CMR Meeting' },
                                { name: "DurationMin", title: 'Meeting Mins', format: formatNumericValueInt },
                                { name: "PeopleMin", title: 'Participant Mins', format: formatNumericValueInt },
                                { name: "CallInMin", title: 'Call In Mins', format: formatNumericValueInt },
                                {
                                    name: "CallOutDomesticMin",
                                    title: 'Call Out Dom. Mins',
                                    format: formatNumericValueInt
                                },
                                {
                                    name: "CallOutInternationalMin",
                                    title: 'Call Out Intl. Mins',
                                    format:
                                    formatNumericValueInt
                                },
                                { name: "TollfreeMin", title: 'Toll Free Mins', format: formatNumericValueInt },
                                { name: "VoipMin", title: 'VOIP Mins', format: formatNumericValueInt },
                                { name: "ParticipantCount", title: 'Participants', format: formatNumericValueInt }
                            ],

                        display: [
                            "ConferenceName", "StartTime", "TelepresenceMeeting", "DurationMin", "PeopleMin", "CallInMin",
                            "CallOutDomesticMin", "CallOutInternationalMin", "TollfreeMin", "VoipMin", "ParticipantCount"
                        ],
                        sortColumn: 'PeopleMin desc',
                        queryId: 'WebexUsersInnerDetails',
                        parentFkColumns: [{ name: 'HostdnUserSID', type: 'numeric' }]
                    }
                ],
                display: [
                    'DisplayName', 'MeetingCount', 'TelepresenceMeetingCount', 'TotalDuration', 'TotalParticipantCount',
                    'TotalPeopleMin', 'TotalCallInMin', 'TotalCallOutDomesticMin', 'TotalCallOutInternationalMin',
                    'TotalTollFreeMin', 'TotalVoipMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true
        });
    },

    getWebexUsersFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexUsersFlexQuery",
            widgetType: "flexquery-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'DisplayName', title: 'Host', type: 'string' },
                    { name: 'meetingcount', title: 'Meetings', type: 'integer' },
                    { name: 'TelepresenceMeetingCount', title: 'CMR Meetings', type: 'integer' },
                    { name: 'totalduration', title: 'Meeting Mins', type: 'integer' },
                    { name: 'TotalParticipantCount', title: 'Participants', type: 'integer' },
                    { name: 'totalpeoplemin', title: 'Participant Mins', type: 'integer' },
                    { name: 'TotalCallInMin', title: 'Total Call In Mins', type: 'integer' },
                    { name: 'TotalCallOutDomesticMin', title: 'Call Out Dom. Mins', type: 'integer' },
                    { name: 'TotalCallOutInternationalMin', title: 'Call Out Intl. Mins', type: 'integer' },
                    { name: 'TotalTollFreeMin', title: 'Toll Free Mins', type: 'integer' },
                    { name: 'TotalVoipMin', title: 'VOIP Mins', type: 'integer' }
                ]
            },
            fixedPanel: true
        });
    },

    ////////////////////// WEBEX PARTICIPANTS //////////////////////

    getWebexParticipantsDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexMeetingParticipants",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 1,
                sortAsc: true,
                columnOptions: [
                    { name: '_rowID', title: 'Row Id', idCol: true, hideFromSelectList: true },
                    { name: 'ParticipantName', title: 'Participant Name', idCol: true, format: formatLink },
                    { name: 'CountryName', title: 'Country Name', idCol: true },
                    { name: 'Region', title: 'Region', idCol: true },
                    {
                        name: 'MeetingCount',
                        title: 'Meetings',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'DurationMin',
                        title: 'Duration (Min)',
                        format: formatNumericValueInt
                    },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "ConferenceName", title: 'Meeting Name' },
                                { name: "StartTime", title: 'Start Time', format: formatDate },
                                { name: "HostName", title: 'Host' },
                                { name: "DurationMin", title: 'Meeting Mins' },
                                { name: "PeopleMin", title: 'Participant Mins' },
                                { name: "ParticipantCount", title: 'Participants' }
                            ],
                        display: [
                            "ConferenceName", "StartTime", "HostName", "DurationMin", "PeopleMin",
                            "ParticipantCount"
                        ],
                        sortColumn: 'StartTime ASC',
                        queryId: 'WebexMeetingParticipantsInnerDetails',
                        parentFkColumns: [
                            { name: 'ParticipantName', type: 'string' }, { name: 'CountryName', type: 'string' },
                            { name: 'Region', type: 'string' }
                        ]
                    }
                ],
                display: ['ParticipantName', 'CountryName', 'Region', 'MeetingCount', 'DurationMin']
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getWebexParticipantsFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "WebexParticipantsFlexQuery",
            widgetType: "flexquery-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: '_rowID', title: 'Row Id', idCol: true, hideFromSelectList: true },
                    { name: 'ParticipantName', title: 'Participant Name', type: 'string' },
                    { name: 'CountryName', title: 'Country Name', type: 'string' },
                    { name: 'Region', title: 'Region', type: 'string' },
                    {
                        name: 'MeetingCount',
                        title: 'Meetings',
                        type: 'integer'
                    },
                    {
                        name: 'DurationMin',
                        title: 'Duration (Min)',
                        type: 'integer'
                    }
                ]
            },
            fixedPanel: true
        });
    },

    ///////////////////////// HISTORICAL MEETINGS ///////////////////////////////////

    ///////// MEETINGS TAB /////////////
    getHistoricalMeetingsSummaryKpiMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingsSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical Participant Stats",
            width: 12,
            height: 1,
            header: "Historical Participant Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Summary Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalMeetingCount', title: 'Meetings', format: formatNumericValueInt0Default },
                    { name: 'TotalMeetingMin', title: 'Meeting Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalParticipantCount', title: 'Participants', format: formatNumericValueInt0Default },
                    {
                        name: 'TotalParticipantMin',
                        title: 'Participant Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'TotalParticipantVideoMin',
                        title: 'Participant Video Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'TotalParticipantAudioMin',
                        title: 'Participant Audio Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    { name: 'AvgMeetingMin', title: 'Avg Meeting Mins', format: formatNumericValueWithSuffix0Default },
                    {
                        name: 'AvgParticipantMin',
                        title: 'Avg Participant Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'AvgParticipantPerMeeting',
                        title: 'Avg Participants/Meeting',
                        format: formatNumericValue0Default
                    }
                ],
                display: [
                    'TotalMeetingCount', 'TotalMeetingMin', 'TotalParticipantCount', 'TotalParticipantMin',
                    'TotalParticipantVideoMin', 'TotalParticipantAudioMin', 'AvgMeetingMin', 'AvgParticipantMin',
                    'AvgParticipantPerMeeting'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalMeetingParticipantSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingsDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingsDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    { name: 'MeetingSID', title: 'MeetingSID', idCol: true, hideFromSelectList: true },
                    { name: 'meetingName', title: 'Meeting Name', widthRatio: 1.5, format: formatLink },
                    { name: 'SystemName', title: 'System Name' },
                    { name: 'StartTime', title: 'Start Time', format: formatDate, widthRatio: 1.25 },
                    { name: 'EndTime', title: 'End Time', format: formatDate, widthRatio: 1.25 },
                    { name: 'DurationMin', title: 'Meeting Mins', format: formatNumericValue2 },
                    { name: 'participantMin', title: 'Participant Mins', format: formatNumericValue2 },
                    { name: 'ParticipantVideoMin', title: 'Participant Video Mins', format: formatNumericValue2 },
                    { name: 'ParticipantAudioMin', title: 'Participant Audio Mins', format: formatNumericValue2 },
                    { name: 'ParticipantCount', title: 'Total Participants', format: formatNumericValueInt },
                    { name: 'participantCountUnique', title: 'Unique Participant', format: formatNumericValueInt },
                    { name: 'CallQuality', title: 'Quality', format: formatQuality },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "participantAddress", title: 'Participant Address', format: formatSpecialLink },
                                { name: "startTime", title: 'Join Time', format: formatDate },
                                { name: "endTime", title: 'Leave Time', format: formatDate },
                                { name: "Protocol", title: 'Protocol' },
                                { name: "CallType", title: 'Call Type' },
                                { name: "DurationMin", title: 'Duration Mins', format: formatNumericValue2 },
                                { name: 'CallQuality', title: 'Quality', format: formatQuality }

                            ],
                        display: ["participantAddress", "startTime", "endTime", "Protocol", "CallType", "DurationMin", "CallQuality"],
                        sortColumn: 'StartTime ASC',
                        queryId: 'HistoricalMeetingInnerDetails',
                        parentFkColumns: [{ name: 'MeetingSID', type: 'numeric' }]
                    }
                ],
                display: [
                    'meetingName', 'SystemName', 'StartTime', 'EndTime', 'DurationMin', 'participantMin',
                    'ParticipantVideoMin',
                    'ParticipantAudioMin', 'ParticipantCount', 'participantCountUnique', 'CallQuality'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingsFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingsFlexQuery",
            widgetType: "historicalmeetingfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'meetingName', title: 'Meeting Name', type: 'string' },
                    { name: 'durationmin', title: 'Meeting Mins', type: 'integer' },
                    { name: 'participantmin', title: 'Participant Mins', type: 'integer' },
                    { name: 'participantvideomin', title: 'Participant Video Mins', type: 'integer' },
                    { name: 'participantaudiomin', title: 'Participant Audio Mins', type: 'integer' },
                    { name: 'participantcount', title: 'Total Participants', type: 'integer' },
                    { name: 'participantcountunique', title: 'Unique Participants', type: 'integer' },
                    { name: 'StartTime', title: 'Start Time', type: 'datetime' },
                    { name: 'EndTime', title: 'End Time', type: 'datetime' },
                    { name: 'CallQuality', title: 'Quality', type: 'string' }
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalfilter-formatter", params: { type: "HistoricalMeetingsFlexQuery" } },
            fixedPanel: true
        });
    },

    ///////// VMR TAB //////////////////
    getHistoricalMeetingVMRSummaryMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingVMRSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical VMR Stats",
            width: 12,
            height: 1,
            header: "Historical VMR Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical VMR Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalVMRCount', title: 'VMRs', format: formatNumericValueInt0Default },
                    { name: 'TotalMeetingCount', title: 'Meetings', format: formatNumericValueInt0Default },
                    { name: 'TotalParticipantCount', title: 'Participants', format: formatNumericValueInt0Default },
                    { name: 'TotalParticipantMin', title: 'Total Mins', format: formatNumericValueWithSuffix0Default },
                    {
                        name: 'TotalParticipantVideoMin',
                        title: 'Video Mins',
                        format: formatNumericValueWithSuffix0Default
                    },
                    {
                        name: 'TotalParticipantAudioMin',
                        title: 'Audio Mins',
                        format: formatNumericValueWithSuffix0Default
                    }
                ],
                display: [
                    'TotalVMRCount', 'TotalMeetingCount', 'TotalParticipantCount', 'TotalParticipantMin',
                    'TotalParticipantVideoMin', 'TotalParticipantAudioMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalMeetingVMRSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingVMRDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingVMRDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Meetings",
            width: 2,
            height: 5,
            header: "Historical Meetings",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions:
                    [
                        { name: '_rowID', title: 'Row Id', idCol: true, hideFromSelectList: true },
                        { name: 'meetingName', title: 'VMR Name', widthRatio: 2 },
                        { name: 'SystemName', title: 'System Name', widthRatio: 1.5 },
                        { name: 'MeetingCount', title: 'Meetings', format: formatNumericValueInt },
                        { name: 'participantMin', title: 'Total Minutes', format: formatNumericValue2 },
                        {
                            name: 'ParticipantVideoMin',
                            title: 'Participant Video Minutes',
                            format: formatNumericValue2
                        },
                        {
                            name: 'ParticipantAudioMin',
                            title: "Participant Audio Minutes",
                            format: formatNumericValue2
                        },
                        { name: 'ParticipantCount', title: 'Total Participants', format: formatNumericValueInt },
                        { name: 'participantCountUnique', title: 'Unique Participants', format: formatNumericValueInt }
                    ],
                display: [
                    'meetingName', 'SystemName', 'MeetingCount', 'participantMin', 'ParticipantVideoMin',
                    'ParticipantAudioMin',
                    'ParticipantCount', 'participantCountUnique'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingVMRDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingVMRFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingVMRFlexQuery",
            widgetType: "historicalmeetingvmrfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'meetingName', title: 'VMR Name', type: 'string' },
                    { name: 'MeetingCount', title: 'Meetings', type: 'integer' },
                    { name: 'participantMin', title: 'Total Minutes', type: 'integer' },
                    { name: 'ParticipantVideoMin', title: 'Participant Video Minutes', type: 'integer' },
                    { name: 'ParticipantAudioMin', title: "Participant Audio Minutes", type: 'integer' },
                    { name: 'ParticipantCount', title: 'Total Participants', type: 'integer' },
                    { name: 'participantCountUnique', title: 'Unique Participants', type: 'integer' }
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalfilter-formatter", params: { type: "HistoricalMeetingVMRFlexQuery" } },
            fixedPanel: true
        });
    },

    ///////// PARTICIPANTS TAB /////////
    getHistoricalMeetingParticipantSummaryMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingParticipantSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical Participant Stats",
            width: 12,
            height: 1,
            header: "Historical Participant Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Summary Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalCalls', title: 'Total Participants', format: formatNumericValueWithSuffix0Default },
                    {
                        name: 'UniqueParticipants',
                        title: 'Unique Participants',
                        format: formatNumericValueWithSuffix0Default
                    },
                    { name: 'TotalMinutes', title: 'Total Minutes', format: formatNumericValueWithSuffix0Default },
                    { name: 'AudioMinutes', title: 'Audio Minutes', format: formatNumericValueWithSuffix0Default },
                    { name: 'VideoMinutes', title: 'Video Minutes', format: formatNumericValueWithSuffix0Default }
                ],
                display: [
                    'TotalCalls', 'UniqueParticipants', 'TotalMinutes', 'AudioMinutes', 'VideoMinutes'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalMeetingParticipantSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingParticipantDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingParticipantDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    { name: '_rowID', title: 'ID', idCol: true, hideFromSelectList: true },
                    { name: 'participantAddress', title: 'Participant Address', widthRatio: 2, format: formatLink },
                    { name: 'participantDisplayName', title: 'Display Name', widthRatio: 2 },
                    {
                        name: 'MeetingName',
                        title: 'Meeting Name',
                        widthRatio: 2,
                        format: formatSecondaryLink,
                        clickActionCallback: participantNavigation
                    },
                    { name: 'startTime', title: 'Start Time', format: formatDate },
                    { name: 'DurationMin', title: 'Total Minutes', format: formatNumericValue2 },
                    { name: 'Protocol', title: 'Protocol' },
                    { name: 'CallType', title: 'Call Type' },
                    { name: 'callDirection', title: 'Call Direction' },
                    { name: 'ServerName', title: 'Server Name' },
                    { name: 'systemType', title: 'System Type', idCol: true, hideFromSelectList: true },
                    { name: 'CallQuality', title: 'Quality', format: formatQuality },
                    { name: 'CQRSID', title: 'CQRSID', idCol: true, hideFromSelectList: true },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "StreamType", title: 'Stream Type' },
                                { name: "StreamBand", title: 'Stream Band', hideFromSelectList: true },
                                { name: "Codec", title: 'Codec' },
                                { name: "AvgBitRate", title: 'Avg. Bit Rate (kbps)', format: formatBitRateValue },
                                { name: "MaxBitRate", title: 'Max Bit Rate (kbps)', format: formatBitRateValue },
                                { name: "AvgFrameRate", title: 'Avg. Frame Rate', format: formatNumericValue2 },
                                { name: "MaxFrameRate", title: 'Max Frame Rate', format: formatNumericValue2 },
                                { name: "Resolution", title: 'Resolution' },
                                { name: "AvgJitter", title: 'Avg. Jitter (ms)', format: formatJitterValue },
                                //{ name: "MaxJitter", title: 'Max Jitter (ms)', format: formatNumericValue2 },
                                { name: "AvgPacketLoss", title: 'Avg. Packet Loss (%)', format: formatPacketLossValue },
                                //{ name: "MaxPacketLoss", title: 'Max Packet Loss (%)', format: formatNumericValue2 },
                                { name: "CallQuality", title: 'Quality', format: formatQuality }
                            ],
                        display: [
                            "StreamType", "Codec", "AvgBitRate", "MaxBitRate", "AvgFrameRate", "MaxFrameRate", "Resolution",
                            "AvgJitter", "AvgPacketLoss", "CallQuality"
                        ],
                        sortColumn: 'StreamBand ASC',
                        queryId: 'HistoricalMeetingParticipantQualityDetails',
                        parentFkColumns: [{ name: 'CQRSID', type: 'numeric' }, { name: 'systemType', type: 'string' }]
                    }
                ],
                display: [
                    'participantAddress', 'participantDisplayName', 'MeetingName', 'startTime', 'DurationMin', 'Protocol', 'CallType',
                    'callDirection',
                    'ServerName', 'CallQuality'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalMeetingParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingParticipantFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingParticipantFlexQuery",
            widgetType: "historicalmeetingparticipantfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'participantaddress', title: 'Participant Address', type: 'string' },
                    { name: 'participantDisplayName', title: 'Display Name', type: 'string' },
                    { name: 'meetingName', title: 'Meeting Name', type: 'string' },
                    { name: 'DurationMin', title: 'Total Minutes', type: 'integer' },
                    { name: 'callDirection', title: 'Call Direction', type: 'string' },
                    { name: 'callquality', title: 'Quality', type: 'string' },
                    { name: 'startTime', title: 'Start Time', type: 'datetime' },
                    { name: 'servername', title: 'Server Name', type: 'string' }
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/historicalfilter-formatter",
                params: { type: "HistoricalMeetingParticipantFlexQuery" }
            },
            fixedPanel: true
        });
    },

    ///////////////////////// HISTORICAL ENDPOINTS ///////////////////////////////////

    /////// MANAGED ENDPOINTS TAB //////////
    getHistoricalManagedEndpointsSummaryKpiMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalManagedEndpointsSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical Participant Stats",
            width: 12,
            height: 1,
            header: "Historical Participant Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Summary Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalEndpoints', title: 'Total Endpoints', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalCalls', title: 'Total Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'VideoCalls', title: 'Video Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'AudioOnlyCalls', title: 'Audio Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalMin', title: 'Total Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'VideoMin', title: 'Video Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'UtilizationPerc', title: 'Avg Utilization %', format: formatPercentage },
                ],
                display: [
                    'TotalEndpoints', 'TotalCalls', 'VideoCalls', 'AudioOnlyCalls', 'TotalMin', 'VideoMin', 'AudioOnlyMin',
                    'UtilizationPerc'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalEndpointParticipantSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalManagedEndpointsDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalManagedEndpointsDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    { name: 'EndpointID', title: 'EndpointID', idCol: true, hideFromSelectList: true },
                    { name: 'Name', title: 'Name', format: formatLink, widthRatio: 2 },
                    { name: 'SubCategory', title: 'Category' },
                    { name: 'Model', title: 'Model' },
                    { name: 'TotalCallCount', title: 'Total Calls', format: formatNumericValueInt },
                    { name: 'VideoCallCount', title: 'Video Calls', format: formatNumericValueInt },
                    { name: 'AudioOnlyCallCount', title: 'Audio Calls', format: formatNumericValueInt },
                    { name: 'TotaldurationMin', title: "Total Mins", format: formatNumericValue2 },
                    { name: 'VideoMin', title: 'Video Mins', format: formatNumericValue2 },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', format: formatNumericValue2 },
                    { name: 'UtilizationPct', title: 'Utilization Rate', format: formatPercentage },
                    {
                        name: 'failCallCount',
                        title: 'Call Issues'
                    },
                    {
                        name: 'badQualityCallCount',
                        title: 'Quality Issues'
                    },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "RemoteAddress", title: 'Remote Address' },
                                { name: "StartTime", title: 'Start Time', format: formatDate },
                                { name: "EndTime", title: 'End Time', format: formatDate },
                                { name: "CallDirection", title: 'Call Direction' },
                                { name: "CallType", title: 'Call Type' },
                                { name: "DurationMin", title: 'Duration Mins', format: formatNumericValue2 },
                                { name: "ExternalFlag", title: 'External Call' },
                            ],
                        display: [
                            "RemoteAddress", "StartTime", "EndTime", "CallDirection", "CallType", "DurationMin",
                            "ExternalFlag"
                        ],
                        sortColumn: 'StartTime desc',
                        queryId: 'HistoricalManagedEndpointInnerDetails',
                        parentFkColumns: [{ name: 'EndpointID', type: 'numeric' }]
                    }
                ],
                display: [
                    'Name', 'SubCategory', 'Model', 'TotalCallCount', 'VideoCallCount', 'AudioOnlyCallCount',
                    'TotaldurationMin', 'VideoMin', 'AudioOnlyMin', 'UtilizationPct', 'failCallCount', 'badQualityCallCount'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalEndpointParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalManagedEndpointsFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalManagedEndpointsFlexQuery",
            widgetType: "historicalmanagedendpointfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'Name', title: 'Name', type: 'string' },
                    { name: 'TotalCallCount', title: 'Total Calls', type: 'integer' },
                    { name: 'VideoCallCount', title: 'Video Calls', type: 'integer' },
                    { name: 'AudioOnlyCallCount', title: 'Audio Calls', type: 'integer' },
                    { name: 'TotaldurationMin', title: "Total Mins", type: 'integer' },
                    { name: 'VideoMin', title: 'Video Mins', type: 'integer' },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', type: 'integer' },
                    { name: 'UtilizationPct', title: 'Utilization %', type: 'integer' },
                    { name: 'failCallCount', title: 'Call Issues', type: 'integer' },
                    { name: 'badQualityCallCount', title: 'Quality Issues', type: 'integer' },
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/historicalfilter-formatter",
                params: { type: "HistoricalManagedEndpointsFlexQuery" }
            },
            fixedPanel: true
        });
    },

    /////// UNMANAGED ENDPOINTS TAB ////////
    getHistoricalUnmanagedEndpointSummaryMulticellTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUnmanagedEndpointSummary",
            widgetType: "kpimulticell-widget",
            title: "Historical VMR Stats",
            width: 12,
            height: 1,
            header: "Historical VMR Stats",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical VMR Stats.",
            maxCount: 1,
            optionsComp: "webexkpi-options",
            defaultOptions: {
                columnOptions: [
                    { name: 'TotalEndpoints', title: 'Total Aliases', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalCalls', title: 'Total Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'VideoCalls', title: 'Video Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'AudioOnlyCalls', title: 'Audio Calls', format: formatNumericValueWithSuffix0Default },
                    { name: 'TotalMin', title: 'Total Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'VideoMin', title: 'Video Mins', format: formatNumericValueWithSuffix0Default },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', format: formatNumericValueWithSuffix0Default }
                ],
                display: [
                    'TotalEndpoints', 'TotalCalls', 'VideoCalls', 'AudioOnlyCalls', 'TotalMin', 'VideoMin', 'AudioOnlyMin'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/kpimulticell-formatter", params: { type: "HistoricalEndpointVMRSummary" } },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalUnmanagedEndpointDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUnmanagedEndpointDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Meetings",
            width: 2,
            height: 5,
            header: "Historical Meetings",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            showInnerGrid: true,
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    { name: 'EndpointID', title: 'EndpointID', idCol: true, hideFromSelectList: true },
                    { name: 'Name', title: 'Name', format: formatLink, widthRatio: 2 },
                    { name: 'Type', title: 'Type' },
                    { name: 'TotalCallCount', title: 'Total Calls', format: formatNumericValueInt },
                    { name: 'VideoCallCount', title: 'Video Calls', format: formatNumericValueInt },
                    { name: 'AudioOnlyCallCount', title: 'Audio Calls', format: formatNumericValueInt },
                    { name: 'TotaldurationMin', title: "Total Mins", format: formatNumericValue2 },
                    { name: 'VideoMin', title: 'Video Mins', format: formatNumericValue2 },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', format: formatNumericValue2 },
                    {
                        name: 'failCallCount',
                        title: 'Call Issues'
                    },
                    {
                        name: 'badQualityCallCount',
                        title: 'Quality Issues'
                    },
                    {
                        name: 'innerGridData',
                        columnOptions:
                            [
                                { name: "RemoteAddress", title: 'Remote Address' },
                                { name: "StartTime", title: 'Start Time', format: formatDate },
                                { name: "EndTime", title: 'End Time', format: formatDate },
                                { name: "CallDirection", title: 'Call Direction' },
                                { name: "CallType", title: 'Call Type' },
                                { name: "DurationMin", title: 'Duration Mins', format: formatNumericValue2 },
                                { name: "ExternalFlag", title: 'External Call' },
                            ],
                        display: [
                            "RemoteAddress", "StartTime", "EndTime", "CallDirection", "CallType", "DurationMin",
                            "ExternalFlag"
                        ],
                        sortColumn: 'StartTime desc',
                        queryId: 'HistoricalUnmanagedEndpointInnerDetails',
                        parentFkColumns: [{ name: 'EndpointID', type: 'numeric' }]
                    }
                ],
                display: [
                    'Name', 'Type', 'TotalCallCount', 'VideoCallCount', 'AudioOnlyCallCount', 'TotaldurationMin',
                    'VideoMin',
                    'AudioOnlyMin', 'failCallCount', 'badQualityCallCount'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalEndpointVMRDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalUnmanagedEndpointFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUnmanagedEndpointFlexQuery",
            widgetType: "historicalunmanagedendpointfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'Name', title: 'Name', type: 'string' },
                    { name: 'Type', title: 'Type', type: 'string' },
                    { name: 'TotalCallCount', title: 'Total Calls', type: 'integer' },
                    { name: 'VideoCallCount', title: 'Video Calls', type: 'integer' },
                    { name: 'AudioOnlyCallCount', title: 'Audio Calls', type: 'integer' },
                    { name: 'TotaldurationMin', title: "Total Mins", type: 'integer' },
                    { name: 'VideoMin', title: 'Video Mins', type: 'integer' },
                    { name: 'AudioOnlyMin', title: 'Audio Mins', type: 'integer' },
                    { name: 'failCallCount', title: 'Call Issues', type: 'integer' },
                    { name: 'badQualityCallCount', title: 'Quality Issues', type: 'integer' },
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/historicalfilter-formatter",
                params: { type: "HistoricalUnmanagedEndpointFlexQuery" }
            },
            fixedPanel: true
        });
    },

    /////// INVENTORY TAB //////////////////
    getHistoricalEndpointInventoryDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointInventoryDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [
                    { name: '_rowID', title: 'Row Id', idCol: true, hideFromSelectList: true },
                    { name: 'SystemName', title: 'System Name' },
                    { name: 'SystemSubCategory', title: 'Sub Category' },
                    { name: 'IPAddress', title: 'IP Address' },
                    { name: 'SerialNumber', title: 'Serial Number' },
                    { name: 'SoftwareVersion', title: 'Software Version' },
                    { name: 'Manufacturer', title: 'Manufacturer' },
                    { name: 'Model', title: 'Model' },
                    { name: 'vulnerability', title: 'Vulnerability', format: formatRed },
                ],
                display: [
                    'SystemName', 'SystemSubCategory', 'IPAddress', 'SerialNumber', 'SoftwareVersion', 'Manufacturer',
                    'Model'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalEndpointParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalEndpointInventoryFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointInventoryFlexQuery",
            widgetType: "historicalendpointinventoryfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'SystemName', title: 'System Name', type: 'string' },
                    { name: 'IPAddress', title: 'IP Address', type: 'string' },
                    { name: 'SerialNumber', title: 'Serial Number', type: 'string' },
                    { name: 'softwareversion', title: 'Software Version', type: 'string' },
                    { name: 'Manufacturer', title: 'Manufacturer', type: 'string' },
                    { name: 'vulnerability', title: 'Vulnerability', type: 'string' },
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/historicalfilter-formatter",
                params: { type: "HistoricalEndpointInventoryFlexQuery" }
            },
            fixedPanel: true
        });
    },

    ///////////////////////// HISTORICAL Issues ///////////////////////////////////

    getHistoricalIssuesDetailsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalIssuesDetails",
            widgetType: "searchgrid2-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "searchgrid2-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 1,
                sortAsc: true,
                columnOptions: [
                    { name: '_rowID', title: 'Row Id', idCol: true, hideFromSelectList: true },
                    { name: 'SourceCallerAddress', title: 'Source Caller', widthRatio: 1.5 },
                    { name: 'SourceCallerSID', title: 'SourceCallerSID', hideFromSelectList: true },
                    { name: 'TargetCallerAddress', title: 'Target Caller', widthRatio: 1.5 },
                    { name: 'TargetCallerSID', title: 'TargetCallerSID', hideFromSelectList: true },
                    { name: 'StartTime', title: 'Start Time', format: formatDate },
                    { name: 'EndTime', title: 'End Time', format: formatDate },
                    { name: 'CallBandwidth', title: 'Bandwidth', widthRatio: 0.7 },
                    { name: 'CallType', title: 'Call Type', widthRatio: 0.5 },
                    { name: 'DurationMin', title: 'Minutes', widthRatio: 0.5, format: formatNumericValue2 },
                    { name: 'callSuccessCategory', title: 'Status', format: formatStatusDiv },
                    { name: 'Reason', title: 'Disconnect Reason' },
                    { name: 'BandCaption', title: 'Packet Loss Band' },
                    { name: 'PacketLossPct', title: 'Packet Loss %', widthRatio: 0.8, format: formatPercentage }
                ],
                display: [
                    'SourceCallerAddress', 'TargetCallerAddress', 'StartTime', 'EndTime', 'CallBandwidth', 'CallType',
                    'DurationMin', 'callSuccessCategory', 'Reason', 'BandCaption', 'PacketLossPct'
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/searchgrid2-formatter",
                params: { type: "HistoricalEndpointParticipantDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalIssuesFlexQueryTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalIssuesFlexQuery",
            widgetType: "historicalissuesfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [
                    { name: 'endpointsid', title: 'Endpoint SID', type: 'uuid', hidden: true },
                    { name: 'SourceCallerAddress', title: 'Source Caller', type: 'string' },
                    { name: 'TargetCallerAddress', title: 'Target Caller', type: 'string' },
                    { name: 'StartTime', title: 'Start Time', type: 'datetime' },
                    { name: 'EndTime', title: 'End Time', type: 'datetime' },
                    { name: 'CallBandwidth', title: 'Bandwidth', type: 'string' },
                    { name: 'CallType', title: 'Call Type', type: 'string' },
                    { name: 'DurationMin', title: 'Minutes', type: 'integer' },
                    { name: 'PacketLossPct', title: 'Packet Loss %', type: 'integer' },
                ]
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalfilter-formatter", params: { type: "HistoricalIssuesFlexQuery" } },
            fixedPanel: true
        });
    },

    ///////  CAPACITY DETAILS

    getHistoricalCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalCapacityDetailsTrend",
            widgetType: "historicalgraph-widget",
            width: 2,
            height: 5,
            description: "The panel provides all sorts of reports.",
            maxCount: 1000,
            defaultOptions:
                {
                    type: "Trend",
                    selectedDisplayType: "Line Trend",
                    selectedMetricColumnNames: ["fctcapacitydetail.capacityutilizeratesystem"],
                    selectedAggregations: ['sum'],
                    selectedGroupColumnNames: ["dimsystem.systemname", "fctCapacitydetail.reporttime"],
                    selectedTimeGrain: "",
                    resize: false,
                    pageNum: 1,
                    pageSize: 15,
                    sortIndex: 0,
                    sortAsc: true,
                    columnOptions: [],
                },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalgraph-formatter", params: { isPercentage: true } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getHistoricalCapacityDetailsParticipantsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalCapacityDetailsParticipantsXml",
            widgetType: "historicalcapacitydetail-widget",
            title: "Historical Capacity Participant Details",
            width: 2,
            height: 5,
            header: "Historical Capacity Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            panelSettings: {
                dataset: "CapacityParticipantXml",
                queryColumn: "fctcapacityparticipant"
            },
            optionsComp: "summarydetail-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "DetailsOnly", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalClusterCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalClusterCapacityDetailsTrend",
            widgetType: "historicalgraph-widget",
            width: 2,
            height: 5,
            description: "The panel provides all sorts of reports.",
            maxCount: 1000,
            defaultOptions:
                {
                    type: "Trend",
                    selectedDisplayType: "Line Trend",
                    selectedMetricColumnNames: ["fctclustercapacitydetail.clustercapacityutilizeratesystem"],
                    selectedAggregations: ['sum'],
                    selectedGroupColumnNames: ["dimcluster.clustername", "fctClusterCapacitydetail.reporttime"],
                    selectedTimeGrain: "",
                    resize: false,
                    pageNum: 1,
                    pageSize: 15,
                    sortIndex: 0,
                    sortAsc: true,
                    columnOptions: [],
                },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalgraph-formatter", params: { isPercentage: true } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getHistoricalClusterCapacityDetailsParticipantsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalClusterCapacityDetailsParticipantsXml",
            widgetType: "historicalcapacitydetail-widget",
            title: "Historical Capacity Participant Details",
            width: 2,
            height: 5,
            header: "Historical Capacity Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 10,
            panelSettings: {
                dataset: "ClusterCapacityParticipantXml",
                queryColumn: "fctclustercapacityparticipant"
            },
            optionsComp: "summarydetail-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "DetailsOnly", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // trunk
    getHistoricalTrunkCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalTrunkCapacityDetailsTrend",
            widgetType: "historicalgraph-widget",
            width: 2,
            height: 5,
            description: "The panel provides all sorts of reports.",
            maxCount: 1000,
            defaultOptions:
                {
                    type: "Trend",
                    selectedDisplayType: "Line Trend",
                    selectedMetricColumnNames: ["fcttrunkcapacitydetail.trunkcapacitycurrentsystem"],
                    selectedAggregations: ['sum'],
                    selectedGroupColumnNames: ["dimtrunk.name", "fcttrunkcapacitydetail.reporttime"],
                    selectedTimeGrain: "",
                    resize: false,
                    pageNum: 1,
                    pageSize: 15,
                    sortIndex: 0,
                    sortAsc: true,
                    columnOptions: [],
                },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalgraph-formatter", params: { isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getHistoricalTrunkCapacityDetailsParticipantsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalTrunkCapacityDetailsParticipantsXml",
            widgetType: "historicalcapacitydetail-widget",
            title: "Historical Trunk Capacity Participant Details",
            width: 2,
            height: 5,
            header: "Historical Trnk Capacity Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 10,
            panelSettings: {
                dataset: "TrunkCapacityParticipantXml",
                queryColumn: "fcttrunkcapacityparticipant"
            },
            optionsComp: "summarydetail-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "DetailsOnly", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // concurrent calls
    getConcurrentCallsCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: "ConcurrentCallsCapacityDetailsTrend",
            widgetType: "historicalgraph-widget",
            width: 2,
            height: 5,
            defaultOptions:
                {
                    type: "Trend",
                    selectedDisplayType: "Line Trend",
                    selectedMetricColumnNames: ["fctcallpeak.callcurrentsystem"],
                    selectedAggregations: ['sum'],
                    selectedGroupColumnNames: ["dimendpointlocation.location", "fctcallpeak.reporttime"],
                    selectedTimeGrain: "",
                    resize: false,
                    pageNum: 1,
                    pageSize: 15,
                    sortIndex: 0,
                    sortAsc: true,
                    columnOptions: [],
                },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalgraph-formatter", params: { isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getConcurrentCallsCapacityDetailsParticipantsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "ConcurrentCallsCapacityDetailsParticipantsXml",
            widgetType: "historicalcapacitydetail-widget",
            width: 2,
            height: 5,
            panelSettings: {
                dataset: "calldetailxml",
                queryColumn: "fctcall"
            },
            optionsComp: "summarydetail-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "DetailsOnly", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // concurrent bandwidth
    getConcurrentBandwidthCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: "ConcurrentBandwidthCapacityDetailsTrend",
            widgetType: "historicalgraph-widget",
            width: 2,
            height: 5,
            defaultOptions:
                {
                    type: "Trend",
                    selectedDisplayType: "Line Trend",
                    selectedMetricColumnNames: ["fctcallpeak.callcurrentbandwidth"],
                    selectedAggregations: ['sum'],
                    selectedGroupColumnNames: ["dimendpointlocation.location", "fctcallpeak.reporttime"],
                    selectedTimeGrain: "",
                    resize: false,
                    pageNum: 1,
                    pageSize: 15,
                    sortIndex: 0,
                    sortAsc: true,
                    columnOptions: [],
                },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: "src/app/data-formatters/historicalgraph-formatter", params: {isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getConcurrentBandwidthCapacityDetailsParticipantsSearchGridTemplate: function () {
        return new PanelTemplate({
            panelType: "ConcurrentBandwidthCapacityDetailsParticipantsXml",
            widgetType: "historicalcapacitydetail-widget",
            width: 2,
            height: 5,
            panelSettings: {
                dataset: "calldetailxml",
                queryColumn: "fctcall"
            },
            optionsComp: "summarydetail-options",
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "DetailsOnly", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // concurrent meeting peak :: system - by system type
    getSysConcurrentMeetingPeakBySysTypeCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysConcurrentMeetingPeakBySysTypeCapacityDetailsTrend',
            widgetType: 'historicalgraph-widget',
            width: 2,
            height: 5,
            defaultOptions: {
                type: 'Trend',
                selectedDisplayType: 'Line Trend',
                selectedMetricColumnNames: ['fctmeetingpeak.meetingcurrentsystem'],
                selectedAggregations: ['max'],
                selectedGroupColumnNames: ['dimsystem.systemtype', 'fctmeetingpeak.reporttime'],
                selectedTimeGrain: '',
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: 'src/app/data-formatters/historicalgraph-formatter', params: { isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getSysConcurrentMeetingPeakBySysTypeCapacityDetailsTableTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysConcurrentMeetingPeakBySysTypeCapacityDetailsTable',
            widgetType: 'historicalcapacitydetail-widget',
            width: 2,
            height: 5,
            panelSettings: {
                dataset: 'MeetingDetailXML',
                queryColumn: 'fctmeeting'
            },
            optionsComp: 'summarydetail-options',
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultDatasetJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: 'DetailsOnly', datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // concurrent meeting peak :: system - by system name
    getSysConcurrentMeetingPeakBySysNameCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysConcurrentMeetingPeakBySysNameCapacityDetailsTrend',
            widgetType: 'historicalgraph-widget',
            width: 2,
            height: 5,
            defaultOptions: {
                type: 'Trend',
                selectedDisplayType: 'Line Trend',
                selectedMetricColumnNames: ['fctmeetingpeak.meetingcurrentsystem'],
                selectedAggregations: ['max'],
                selectedGroupColumnNames: ['dimsystem.systemname', 'fctmeetingpeak.reporttime'],
                selectedTimeGrain: '',
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: 'src/app/data-formatters/historicalgraph-formatter', params: { isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getSysConcurrentMeetingPeakBySysNameCapacityDetailsTableTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysConcurrentMeetingPeakBySysNameCapacityDetailsTable',
            widgetType: 'historicalcapacitydetail-widget',
            width: 2,
            height: 5,
            panelSettings: {
                dataset: 'MeetingDetailXML',
                queryColumn: 'fctmeeting'
            },
            optionsComp: 'summarydetail-options',
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultDatasetJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: 'DetailsOnly', datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    // concurrent meeting peak :: system type - by system type name
    getSysTypeConcurrentMeetingPeakCapacityDetailsTrendTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysTypeConcurrentMeetingPeakCapacityDetailsTrend',
            widgetType: 'historicalgraph-widget',
            width: 2,
            height: 5,
            defaultOptions: {
                type: 'Trend',
                selectedDisplayType: 'Line Trend',
                selectedMetricColumnNames: ['fctmeetingpeak_system.meetingcurrentsystem'],
                selectedAggregations: ['max'],
                selectedGroupColumnNames: ['dimsystemtype.systemtype', 'fctmeetingpeak_system.reporttime'],
                selectedTimeGrain: '',
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: { name: 'src/app/data-formatters/historicalgraph-formatter', params: { isPercentage: false } },
            fixedPanel: true,
            hideHeader: true,
            hideControls: true
        });
    },

    getSysTypeConcurrentMeetingPeakCapacityDetailsTableTemplate: function () {
        return new PanelTemplate({
            panelType: 'SysTypeConcurrentMeetingPeakCapacityDetailsTable',
            widgetType: 'historicalcapacitydetail-widget',
            width: 2,
            height: 5,
            panelSettings: {
                dataset: 'MeetingDetailXML',
                queryColumn: 'fctmeeting'
            },
            optionsComp: 'summarydetail-options',
            defaultOptions: {
                resize: false,
                pageNum: 1,
                pageSize: 15,
                sortIndex: 0,
                sortAsc: true,
                columnOptions: [],
                display: []
            },
            dataSources: [{
                service: 'DatabaseMethods.aspx',
                method: 'GetQueryResultDatasetJson',
                resType: 'json',
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: 'DetailsOnly', datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    /////// INFRASTUCTURE SUMMARY DETAIL TAB //////////////////
    getHistoricalInfrastructureXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalInfrastructureXml",
            widgetType: "summarydetail-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "dimtrunk.dimtrunksid",
                visibilityOptions: { hideSystems: true },
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalInfrastructureMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalInfrastructureMetadataXml",
            widgetType: "genericfilters-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "SystemXML"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "SystemXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },
    ////////////

    /////// INFRASTUCTURE SUMMARY DETAIL TAB //////////////////
    getHistoricalNetworksXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalNetworksXml",
            widgetType: "summarydetail-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "NetworkXml",
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalNetworksMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalNetworksMetadataXml",
            widgetType: "genericfilters-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "NetworkXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "NetworkXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },
    ////////////
    
    /////// TRUNKS SUMMARY DETAIL TAB //////////////////
    getHistoricalTrunksXmlTemplate: function() {
        return new PanelTemplate({
          panelType: "HistoricalTrunksXml",
          widgetType: "summarydetail-widget",
          width: 2,
          height: 5,
          maxCount: 1,
          optionsComp: "summarydetail-options",
          defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
            dataset: "TrunkXML",
            visibilityOptions: { hideSystems: true }, // TODO: What does this do? Do we need it for Trunks?
          }),
          dataSources: [{
            service: "DatabaseMethods.aspx",
            method: "GetQueryResultDatasetJson",
            resType: "json",
            refreshInterval: 0,
            params: { observableParams: true, obsParamsInitValue: {} }
          }],
          dataFormatter: {
            name: "src/app/data-formatters/xmldetails-formatter",
            params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
          },
          fixedPanel: true,
          optionsLink: editColumnsLink
        });
      },

      getHistoricalTrunksMetadataXmlTemplate: function() {
        return new PanelTemplate({
          panelType: "HistoricalTrunksMetadataXml",
          widgetType: "genericfilters-widget",
          width: 2,
          height: 5,
          maxCount: 1,
          defaultOptions: {
            columnOptions: [],
            dataset: "TrunkXML"
          },
          dataSources: [{
            service: "DatabaseMethods.aspx",
            method: "GetFiltersForFactKey",
            resType: "json",
            refreshInterval: 0,
            params: { observableParams: true, obsParamsInitValue: { datasetName: "TrunkXml" } }
          }],
          dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
          fixedPanel: true
        });
      },
    ////////////

    /////// USER SUMMARY DETAIL TAB //////////////////
    getHistoricalUserXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUserXml",
            widgetType: "summarydetail-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            optionsComp: "summarydetail-options",
            showInnerGrid: true,
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "UserXml",
                visibilityOptions: { hideSystems: true },
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalUserMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUserMetadataXml",
            widgetType: "genericfilters-widget",
            width: 2,
            height: 5,
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "UserXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "UserXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },
    ////////////

    /////// MANAGED ENDPOINT SUMMARY DETAIL TAB //////////////////
    getHistoricalManagedEndpointXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalManagedEndpointXml",
            widgetType: "summarydetail-widget",
            title: "Historical Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            showInnerGrid: true,
            defaultOptions: _.assign({
                dataset: "ManageEndpointXml",
                displayDetail: this.defaults.historical2.gridColumns.ManageEndpointXml
            }, this.defaults.historical2.panelOptions),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: {}
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalManagedEndpointMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalManagedEndpointMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "ManageEndpointXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "ManageEndpointXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: { endpointModelsTreeFilter: true } },
            fixedPanel: true
        });
    },
    ////////////

    /////// ENDPOINT INVENTORY SUMMARY DETAIL TAB //////////////////
    getHistoricalEndpointInventoryXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointInventoryXml",
            widgetType: "summarydetail-widget",
            title: "Historical Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "InventoryXml",
                displayDetail: this.defaults.historical2.gridColumns.InventoryXml,
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: {}
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalEndpointInventoryMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointInventoryMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "ManageEndpointXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "InventoryXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: { endpointModelsTreeFilter: true } },
            fixedPanel: true
        });
    },
    ////////////

    /////// ENDPOINT CALL SUMMARY DETAIL TAB //////////////////
    getHistoricalEndpointCallXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointCallXml",
            widgetType: "summarydetail-widget",
            title: "Historical Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            showInnerGrid: true,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({
                dataset: "CallXml",
                displayDetail: this.defaults.historical2.gridColumns.CallXml
            }, this.defaults.historical2.panelOptions),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalEndpointCallMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalEndpointCallMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "CallXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "CallXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: { endpointModelsTreeFilter: true } },
            fixedPanel: true
        });
    },
    ////////////

    /////// UNMANAGED ENDPOINT SUMMARY DETAIL TAB //////////////////
    getHistoricalUnmanagedEndpointXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUnmanagedEndpointXml",
            widgetType: "summarydetail-widget",
            title: "Historical Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            showInnerGrid: true,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({
                dataset: "UnmanageEndpointXml",
                displayDetail: this.defaults.historical2.gridColumns.UnmanageEndpointXml
            }, this.defaults.historical2.panelOptions),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalUnmanagedEndpointMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalUnmanagedEndpointMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Participant Details",
            width: 2,
            height: 5,
            header: "Historical Participant Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Participant Details.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "UnmanageEndpointXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "UnmanageEndpointXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },
    ////////////

    //////// ROOMS TAB /////////////////////
    getHistoricalRoomsMetadataTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalRoomsMetadata",
            widgetType: "genericfilters-widget",
            title: "Historical Room Filters",
            width: 2,
            height: 5,
            header: "Historical Rooms Filters",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Room Filters.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "scheduleRoomXML"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "scheduleRoomXML" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },

    getHistoricalRoomsTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalRooms",
            widgetType: "summarydetail-widget",
            title: "Historical Room Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Room Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Room Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "scheduleRoomXML"
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },
    ////////////

    //////// BookingS TAB /////////////////////
    getHistoricalBookingsMetadataTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalBookingsMetadata",
            widgetType: "genericfilters-widget",
            title: "Historical Booking Filters",
            width: 2,
            height: 5,
            header: "Historical Bookings Filters",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Booking Filters.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "scheduleMeetingXML"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "scheduleMeetingXML" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: {} },
            fixedPanel: true
        });
    },

    getHistoricalBookingsTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalBookings",
            widgetType: "summarydetail-widget",
            title: "Historical Booking Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Booking Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Booking Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "scheduleMeetingXML"
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },
    ////////////

    /////// MEETING SUMMARY DETAIL TAB //////////////////
    getHistoricalMeetingXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingXml",
            widgetType: "summarydetail-widget",
            title: "Historical Meeting Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Meeting Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Details.",
            maxCount: 1,
            showInnerGrid: true,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({
                dataset: "MeetingXml",
                displayDetail: this.defaults.historical2.gridColumns.MeetingXml
            }, this.defaults.historical2.panelOptions),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Meeting Filters",
            width: 2,
            height: 5,
            header: "Historical Meeting Filters",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Filters.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "MeetingXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "MeetingXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: { systemsTreeFilter: true, systemsCategory: "meeting" } },
            fixedPanel: true
        });
    },
    ////////////

    /////// MEETING VMR SUMMARY DETAIL TAB //////////////////
    getHistoricalMeetingVmrXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingVmrXml",
            widgetType: "summarydetail-widget",
            title: "Historical Meeting Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Meeting Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            defaultOptions: _.assign({}, this.defaults.historical2.panelOptions, {
                dataset: "VmrXml",
                displayDetail: this.defaults.historical2.gridColumns.VmrXml,
            }),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingVmrMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingVmrMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Meeting Filters",
            width: 2,
            height: 5,
            header: "Historical Meeting Filters",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Filters.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "VmrXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "VmrXml" } }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/genericfilter-formatter",
                params: { systemsTreeFilter: true, showOnlyBridges: true, systemsCategory: "vmr" }
            },
            fixedPanel: true
        });
    },
    ////////////

    /////// MEETING PARTICIPANTS SUMMARY DETAIL TAB //////////////////
    getHistoricalMeetingParticipantsXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingParticipantXml",
            widgetType: "summarydetail-widget",
            title: "Historical Meeting Summary and Details",
            width: 2,
            height: 5,
            header: "Historical Meeting Summary and Details",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Details.",
            maxCount: 1,
            optionsComp: "summarydetail-options",
            showInnerGrid: true,
            defaultOptions: _.assign({
                dataset: "ParticipantXml",
                displayDetail: this.defaults.historical2.gridColumns.ParticipantXml
            }, this.defaults.historical2.panelOptions),
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "SummaryAndDetails", datapresorted: true, dataprepaged: true }
            },
            fixedPanel: true,
            optionsLink: editColumnsLink
        });
    },

    getHistoricalMeetingParticipantsMetadataXmlTemplate: function () {
        return new PanelTemplate({
            panelType: "HistoricalMeetingParticipantMetadataXml",
            widgetType: "genericfilters-widget",
            title: "Historical Meeting Filters",
            width: 2,
            height: 5,
            header: "Historical Meeting Filters",
            image: "CallControlStatusUpDownTwoCell.png",
            description: "The panel provides Historical Meeting Filters.",
            maxCount: 1,
            defaultOptions: {
                columnOptions: [],
                dataset: "ParticipantXml"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetFiltersForFactKey",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: { datasetName: "ParticipantXml" } }
            }],
            dataFormatter: { name: "src/app/data-formatters/genericfilter-formatter", params: { systemsTreeFilter: true, systemsCategory: "participant" } },
            fixedPanel: true
        });
    },
    ////////////

    /**
     * FLEXI PANEL
     *
     * options:
     * {
         *     type: null,
         *     defaultDisplayType: null,
         *     panelName: null,
         *     image: null,
         *     title: null,
         *     panelHeader: null,
         *     defaultMetric: null,
         *     defaultGroup: null,
         *     defaultDataset: null,
         *     defaultTimeGrain: null,
         *     defaultCategory: null,
         *     description: null
         * }
     **/
    getFlexiPanelTemplate: function (options) {
        var defaultOptions = {
            type: options.type,
            selectedDisplayType: options.defaultDisplayType,
            defaultCategory: options.defaultCategory,
            resize: true,
            pageNum: 1,
            pageSize: 5,
            sortIndex: 0,
            sortAsc: true
        };
        if (options.defaultMetric) {
            defaultOptions.selectedMetricColumnNames = [options.defaultMetric];
        }
        if (options.defaultGroup) {
            defaultOptions.selectedGroupColumnNames = [options.defaultGroup];
        }
        if (options.defaultDataset) {
            defaultOptions.selectedDatasetNames = [options.defaultDataset];
        }
        if (options.defaultTimeGrain !== null) {
            defaultOptions.selectedTimeGrain = options.defaultTimeGrain;
        }
        return new PanelTemplate({
            panelType: options.panelName || 'FlexiPanel',
            widgetType: "flexipanel-widget",
            title: options.title,
            width: 2,
            height: 6,
            header: options.panelHeader,
            image: options.image,
            imageDark: options.imageDark,
            description: options.description || "The panel provides all sorts of reports",
            maxCount: 1000,
            optionsComp: "flexipanel-options",
            defaultOptions: defaultOptions,
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/flexipanel-formatter",
                params: { type: options.panelName }
            },
            allowAddToDashboard: false
        });
    },

///////////////////////////////////////////////////////////////////////////////
// Start historical2 panels
///////////////////////////////////////////////////////////////////////////////
    getDistributionFlexiPanelTemplate: function () {
        return this.getFlexiPanelTemplate({
            type: 'Pie',
            defaultDisplayType: 'Pie',
            panelName: 'Distribution',
            image: 'cpma-pie-light.png',
            imageDark: 'cpma-pie-dark.png',
            title: 'Distribution',
            panelHeader: 'Distribution',
            description: 'Distribution panels to compare data categories and sets.'
        });
    },

    getTrendFlexiPanelTemplate: function () {
        return this.getFlexiPanelTemplate({
            type: 'Trend',
            defaultDisplayType: 'Area Trend',
            panelName: 'Trend',
            image: 'cpma-trend-light.png',
            imageDark: 'cpma-trend-dark.png',
            title: 'Trend',
            panelHeader: 'Trend',
            description: 'Trend panels show data trends over time.'
        });
    },

    getSummaryFlexiPanelTemplate: function () {
        var defaultOptions = {};

        defaultOptions.type = "Table";

        var panelName = 'Summary';
        var title = 'Summary';
        var panelHeader = 'Summary';
        var image = 'cpma-summary-light.png';
        var imageDark = 'cpma-summary-dark.png';

        defaultOptions.resize = true;
        defaultOptions.pageNum = 1;
        defaultOptions.pageSize = 5;
        defaultOptions.sortIndex = 0;
        defaultOptions.sortAsc = true;

        return new PanelTemplate({
            panelType: panelName,
            widgetType: "flexipanel-widget",
            title: title,
            width: 2,
            height: 10,
            header: panelHeader,
            image: image,
            imageDark: imageDark,
            description: "Table summaries of Endpoints and Infrastructure.",
            maxCount: 1000,
            optionsComp: "flexipanel-options",
            defaultOptions: defaultOptions,
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "Details", datapresorted: true, dataprepaged: true }
            }
        });
    },

    getSummaryPanelTemplate: function () {
        var panelName = 'SummaryPanel';
        var title = 'Summary';
        var panelHeader = 'Summary';
        var image = 'cpma-summary-light.png';
        var imageDark = 'cpma-summary-dark.png';

        var defaultOptions = {
            type: "Table",
            resize: true,
            pageNum: 1,
            pageSize: 10,
            sortIndex: 0,
            sortAsc: true,
            panelFilters: true,
        };

        return new PanelTemplate({
            panelType: panelName,
            widgetType: "flexipanel-widget",
            title: title,
            width: 2,
            height: 8,
            header: panelHeader,
            image: image,
            imageDark: imageDark,
            description: "Table summaries of Endpoints and Infrastructure.",
            maxCount: 1000,
            optionsComp: "flexipanel-options",
            defaultOptions: defaultOptions,
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/xmldetails-formatter",
                params: { type: "Details", datapresorted: true, dataprepaged: true }
            }
        });
    },

    getKPIPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "KPIPanel",
            widgetType: "kpipanel-widget",
            title: "KPI",
            width: 1,
            height: 2,
            header: "KPI",
            image: "cpma-kpi-light.png",
            imageDark: "cpma-kpi-dark.png",
            description: "Key metrics with growth indicators.",
            maxCount: 1000,
            optionsComp: "flexipanel-options",
            defaultOptions: {
                type: "kpipanel-widget"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetKPIQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/kpipanel-formatter", params: { type: "KPIPanel" }
            },
            allowAddToDashboard: false,
            hideHeader: false
        });
    },

    getKpiCellPanelTemplate: function () {
        return new PanelTemplate({
            panelType: "KPICell",
            widgetType: "kpicell-widget",
            title: "KPI",
            width: 1,
            height: 2,
            header: "KPI",
            image: "cpma-kpi-light.png",
            imageDark: "cpma-kpi-dark.png",
            description: "Key metrics with growth indicators.",
            maxCount: 1000,
            optionsComp: "kpicell-options",
            defaultOptions: {
                type: "kpicell-widget"
            },
            dataSources: [{
                service: "DatabaseMethods.aspx",
                method: "GetKPIQueryResultDatasetJson",
                resType: "json",
                refreshInterval: 0,
                params: { observableParams: true, obsParamsInitValue: {} }
            }],
            dataFormatter: {
                name: "src/app/data-formatters/kpicell-formatter", params: { type: "KPICell" }
            },
            allowAddToDashboard: false,
            hideHeader: true
        });
    },

};

function EmptyIfNull(value) {
    return (value ? value : "");
}

function EndpointsOption(endpointsPrefix) {
    return endpointsPrefix ? { isEndpoint: true } : null;
}

///////////////////////////////// FORMATTER FUNCTIONS ///////////////////////////////

function formatBitRateValue(val) {
    return parseFloat(val / 1024).toFixed(2);
}

function formatDate(val) {
    // 2016-04-01T11:14:07.766589
    var datetimeTokens = val.split('T');
    var dateTokens = datetimeTokens[0].split('-');
    var timeTokens = datetimeTokens[1].split(':');

    return dateTokens[1] +
        "/" +
        dateTokens[2] +
        "/" +
        dateTokens[0] +
        " " +
        timeTokens[0] +
        ":" +
        timeTokens[1] +
        ":" +
        (timeTokens[2].split('.')[0]);
}

function formatNumericValueWithSuffix(val, defaultVal) {
    var ranges = [
        { divider: 1e9, suffix: 'B' },
        { divider: 1e6, suffix: 'M' },
        { divider: 1e3, suffix: 'k' }
    ];

    for (var i = 0; i < ranges.length; i++) {
        if (val >= ranges[i].divider) {
            return (val / ranges[i].divider).toFixed(1).toString() + ranges[i].suffix;
        }
    }

    if (isInteger(val)) {
        return val;
    }

    var result = parseFloat(val).toFixed(2);

    if (isNaN(result)) {
        return (defaultVal !== null) ? defaultVal : null;
    }
    return result;
}

function formatNumericValue(val, defaultVal) {
    var result = parseFloat(val).toFixed(1);

    if (isNaN(result)) {
        return (defaultVal !== null) ? defaultVal : null;
    }
    return result;
}

function formatJitterValue(val) {
    var className = "valGreen";
    if (isFloat(val) || isInteger(val)) {
        if (val <= 10.0) {
            className = "valGreen";

        } else if ((val > 10.0) && (val <= 40.0)) {
            className = "valYellow";

        } else {
            className = "valRed";
        }
    }
    return "<div class=" + className + ">" + val + "</div>";
}

function formatPacketLossValue(val) {
    var className = "valGreen";
    if (isFloat(val) || isInteger(val)) {
        if (val <= 0.5) {
            className = "valGreen";

        } else if ((val > 0.5) && (val <= 2.0)) {
            className = "valYellow";

        } else {
            className = "valRed";
        }
    }
    return "<div class=" + className + ">" + parseFloat(val).toFixed(2) + "</div>";
}

function formatNumericValue2(val, defaultVal) {
    var result = parseFloat(val).toFixed(2);
    if (isNaN(result)) {
        return (defaultVal !== null) ? defaultVal : null;
    }
    return result;
}

function formatQuality(val) {
    var className;
    switch (val) {
        case 'Good':
            className = 'qualityGood';
            break;
        case 'Fair':
            className = 'qualityFair';
            break;
        case 'Bad':
            className = 'qualityBad';
            break;
        default:
            className = 'qualityUnknown';
            break;
    }
    return '<span class="' + className + '">' + val + '</span>';
}

function formatPercentage(val, defaultVal) {
    var result = parseFloat(val).toFixed(2);
    if (isNaN(result)) {
        return (defaultVal !== null) ? defaultVal : null;
    }
    return result + "%";
}

function formatNumericValueInt(val, defaultVal) {
    var result = parseInt(val);
    if (isNaN(result)) {
        return (defaultVal !== null) ? defaultVal : null;
    }
    return result;
}

function formatNumericValueWithSuffix0Default(val) {
    return formatNumericValueWithSuffix(val, 0);
}

function formatNumericValue0Default(val) {
    return formatNumericValue(val, 0);
}

function formatNumericValueInt0Default(val) {
    return formatNumericValueInt(val, 0);
}

function formatRed(val) {
    var result = '<span title ="' + val + '" style="color:var(--bad-color)">' + val + '</span>';
    return result;
}

function formatLink(val) {
    var result = '<span title ="' +
        val +
        '" class=".link-text">' +
        val +
        '</span>';
    return result;
}

function formatSecondaryLink(val) {
    var result = '<span title ="' + val + '" style="color:var(--hover-color);cursor:pointer;">' + val + '&nbsp;<span class="flaticon-browser11 icon-popup"></span></span>';
    return result;
}

function formatSpecialLink(val) {
    var result = '';
    var res = val.split('|');
    if (res[0] !== '') {
        result = '<a style="color:var(--hover-color);cursor:pointer" title="' + res[0] + '" href="#historical/meetings/tabid/2/params/inpstartdate|' + res[1] + '&inpenddate|' + res[2] + '&wherecondition|participantaddress=' + res[0] + '">' + res[0] + '&nbsp;<span class="flaticon-browser11 icon-popup"></span></span>';
    }
    return result;
}

function formatStatusDiv(val) {
    var result = "";
    if (val === "Connected") {
        result = '<span class="callStatusGreen">' + val + '</span>';

    } else if (val === "Not Connected") {
        result = '<span class="callStatusOrange">' + val + '</span>';

    } else if (val === "SIP Toll Attack") {
        result = '<span class="callStatusRed">' + val + '</span>';

    } else {
        result = '<span class="callStatusGreen">' + val + '</span>';
    }
    return result;
}

function getPanelsNameFullList() {
    var panelsNameList = {};

    panelsNameList.endpointdetail = [];

    panelsNameList.historical2adoption = [
        "KPIPanel", "Distribution", "Trend", "SummaryPanel", "InfrastructureSummary", "CallCountByEndpointCategoryPie"
    ];

    panelsNameList.historical2capacity = [
        "KPIPanel", "Distribution", "Trend", "SummaryPanel", "InfrastructureSummary", "CallCountByEndpointCategoryPie"
    ];

    panelsNameList.historical2dashboard = [
        "KPIPanel", "Distribution", "Trend", "SummaryPanel"
    ];

    panelsNameList.historical2issues = [
        "KPIPanel", "Distribution", "Trend", "SummaryPanel", "InfrastructureSummary", "CallCountByEndpointCategoryPie"
    ];

    panelsNameList.rtcapacity = [
        "CapacityStatusTrendLineChart", "CapacityStatusGrid", "PexipLocationCapacityLineChart"
    ];

    panelsNameList.rtdashboard = [
        "TopUtilizedBridgesGrid", "TopUtilizedCallControlGrid", "OverallQualityTotalsGrid",
        "BridgeLicenseUtilizationGauge", "CallControlLicenseUtilizationGauge", "LicenseUtilizationLineChart",
        "EndPointStatusUpDownTwoCell", "BridgeStatusUpDownTwoCell", "SkypeStatusUpDownTwoCell", "CallControlStatusUpDownTwoCell"
    ];

    panelsNameList.rtendpoints = ["EndpointStatusKoGrid"];

    panelsNameList.rtscheduledmeetings = ["ScheduledMeetingsGrid"];

    panelsNameList.webexdashboard = ["WebExSummaryStatsMultiCell", "WebExSummaryMeetingsFlexquery"];

    return panelsNameList;
}

function isFloat(n) {
    return n === +n && n !== (n | 0);
}

function isInteger(n) {
    return n === +n && n === (n | 0);
}

function participantNavigation(data, params) {
    let filters = [{
      selectedLhs:'meetingName',
      selectedOperator: '=',
      rhsText: data.MeetingName.replace(/<\/?[^>]+(>|$)/g, ""),
      valueType:'string',
      selectedConjunction: 'AND'
    }];
    let condition = {
      WhereConditionObj: filters,
      WhereConditionConjunction: 'AND',
      inpStartDate: params.inpStartDate,
      inpEndDate: params.inpEndDate
    };
    FilterManager.setCurrentFilters(JSON.stringify(condition));
    location.href = '#historical/meetings/tabid/0/params/hasFilters|true';
}

export default PanelTemplateFactory;
